/**
 * DTF Gang Sheet Builder — 60ft Ink
 *
 * Client-side v1:
 *   - Customer uploads multiple design files (drag-drop or file picker)
 *   - Each design is uploaded to R2 via the worker's /api/customer-upload
 *     endpoint (returns { url } for the stored file)
 *   - Customer sets a print width in cm for each design; height auto-derives
 *     from the file's native aspect ratio
 *   - We shelf-pack the designs into a fixed-width sheet (default 57cm) and
 *     compute the total length + area used
 *   - Price = width × length × price_per_cm² (floored at min charge)
 *   - Add to cart: pushes N × $1 units of the DTF Gang Sheet variant so the
 *     cart total lands on the correct dollar figure. Cart consolidator
 *     (in ai-designer.js) hides those raw lines and shows a summary row.
 *
 * All dimensions internally are in mm for precision. Displayed in cm.
 */
(function () {
  const root = document.querySelector("[data-gang-sheet-builder]");
  if (!root) return;

  // ============= Config from Liquid section settings =============
  // Tiered per-linear-cm pricing — the sheet's total length falls into
  // exactly one tier, and that tier's rate applies to the WHOLE length
  // (not bracket-style). Matches Amazing Transfers' pricing model:
  //   0–99cm    → $0.41/cm
  //   100–199cm → $0.32/cm
  //   200cm+    → $0.29/cm
  const CFG = {
    workerUrl: (root.dataset.workerUrl || "").replace(/\/$/, ""),
    sheetWidthCm: parseFloat(root.dataset.sheetWidthCm) || 57,
    tier1MaxCm: parseFloat(root.dataset.tier1MaxCm) || 99,
    tier1Rate: parseFloat(root.dataset.tier1Rate) || 0.40,
    tier2MaxCm: parseFloat(root.dataset.tier2MaxCm) || 199,
    tier2Rate: parseFloat(root.dataset.tier2Rate) || 0.31,
    tier3Rate: parseFloat(root.dataset.tier3Rate) || 0.28,
    minChargeAud: parseFloat(root.dataset.minChargeAud) || 15,
    minSheetLengthCm: parseFloat(root.dataset.minSheetLengthCm) || 10,
    // Floor of 5000cm enforced — a stale section setting saved when the
    // default was 500cm was blocking large orders. Section owners can still
    // raise it above 5000 via the theme editor if they need to.
    maxSheetLengthCm: Math.max(5000, parseFloat(root.dataset.maxSheetLengthCm) || 5000),
    // 6mm floor — production minimum. If the theme setting is anything lower
    // (or missing), we still use 6mm to guarantee reliable weeding + heat
    // press margin between transfers.
    gapMm: Math.max(6, parseFloat(root.dataset.gapMm) || 6),
    // Design files are assumed to be at this DPI. Standard for DTF prints
    // is 300 DPI — files at that resolution give correct print size when
    // we do: cm = pixels ÷ dpi × 2.54.
    designDpi: parseFloat(root.dataset.designDpi) || 300,
    variantId: root.dataset.variantId || "",
    // GST display — Roland's store is set up to add GST at checkout, but
    // AU consumer convention is to show inclusive prices upfront so customers
    // aren't surprised. When showGstInclusive is true, all displayed prices
    // are multiplied by (1 + gstPercent/100). The cart quantity / underlying
    // rate is unchanged — Shopify still calculates + adds GST at checkout.
    gstPercent: parseFloat(root.dataset.gstPercent) || 10,
    showGstInclusive: (root.dataset.showGstInclusive || "true").toLowerCase() !== "false",
    // Optional URL of the server-side gang sheet compositor (Fly.io service).
    // When set, we skip the browser-side canvas rendering entirely and hand
    // the layout JSON to the server, which returns a single 300 DPI print
    // file. Removes browser memory + DPI constraints. If unset, falls back
    // to the legacy in-browser tile rendering.
    compositorUrl: (root.dataset.compositorUrl || "").replace(/\/$/, ""),
  };
  const CM_PER_INCH = 2.54;

  const MM_PER_CM = 10;
  const SHEET_WIDTH_MM = CFG.sheetWidthCm * MM_PER_CM;
  const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25MB max per file
  const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

  // ============= State =============
  /** @type {Array<{id: string, file: File, url: string, nativeW: number, nativeH: number, widthCm: number, heightCm: number, aspectRatio: number, quantity: number, uploadedUrl: string|null, status: 'uploading'|'ok'|'error', errorMessage?: string, previewDataUrl: string}>} */
  const designs = [];
  let layout = { placed: [], totalLengthMm: 0, areaCm2: 0 };
  let uploadedFileCounter = 0;

  // Preview background colour — customer-adjustable via swatches under the
  // canvas. This is PREVIEW ONLY: the composite PNG uploaded to R2 keeps a
  // transparent background so DTF prints correctly. Persisted per-browser via
  // localStorage so it survives a page refresh.
  const DEFAULT_PREVIEW_BG = "#f5f3ee";
  const BG_STORAGE_KEY = "gsb_preview_bg";
  let previewBg = DEFAULT_PREVIEW_BG;
  try {
    const saved = localStorage.getItem(BG_STORAGE_KEY);
    if (saved && /^#[0-9a-fA-F]{6}$/.test(saved)) previewBg = saved;
  } catch (_) { /* localStorage disabled — stick with default */ }

  // ============= Elements =============
  const el = {
    restoreBanner: root.querySelector("[data-gsb-restore-banner]"),
    restoreSummary: root.querySelector("[data-gsb-restore-summary]"),
    restoreClear: root.querySelector("[data-gsb-restore-clear]"),
    restoreDismiss: root.querySelector("[data-gsb-restore-dismiss]"),
    drop: root.querySelector("[data-gsb-drop]"),
    fileInput: root.querySelector("[data-gsb-file-input]"),
    uploadError: root.querySelector("[data-gsb-upload-error]"),
    designsStep: root.querySelector("[data-gsb-designs-step]"),
    designsList: root.querySelector("[data-gsb-designs]"),
    addMore: root.querySelector("[data-gsb-add-more]"),
    designTemplate: document.querySelector("[data-gsb-design-template]"),
    previewEmpty: root.querySelector("[data-gsb-preview-empty]"),
    previewInner: root.querySelector("[data-gsb-preview-inner]"),
    canvas: root.querySelector("[data-gsb-canvas]"),
    bgSwatches: root.querySelectorAll("[data-gsb-bg-swatch]"),
    bgCustomInput: root.querySelector("[data-gsb-bg-custom]"),
    lengthLabel: root.querySelector("[data-gsb-length]"),
    countLabel: root.querySelector("[data-gsb-count]"),
    rateLabel: root.querySelector("[data-gsb-rate]"),
    priceLabel: root.querySelector("[data-gsb-price]"),
    tierRows: root.querySelectorAll("[data-gsb-tier]"),
    canvasDimensions: root.querySelector("[data-gsb-canvas-dimensions]"),
    canvasEfficiency: root.querySelector("[data-gsb-canvas-efficiency]"),
    addToCartBtn: root.querySelector("[data-gsb-add-to-cart]"),
    cartError: root.querySelector("[data-gsb-cart-error]"),
    compositeProgress: root.querySelector("[data-gsb-composite-progress]"),
    compositeProgressText: root.querySelector("[data-gsb-composite-progress-text]"),
    // Approval modal — customer eyeballs the composite before we commit to cart.
    approvalModal: document.querySelector("[data-gsb-approval-modal]"),
    approvalPreview: document.querySelector("[data-gsb-approval-preview]"),
    approvalPreviewCanvas: document.querySelector("[data-gsb-approval-preview-canvas]"),
    approvalPreviewLoading: document.querySelector("[data-gsb-approval-preview-loading]"),
    approvalPreviewNote: document.querySelector("[data-gsb-approval-preview-note]"),
    approvalDims: document.querySelector("[data-gsb-approval-dims]"),
    approvalSize: document.querySelector("[data-gsb-approval-size]"),
    approvalUnique: document.querySelector("[data-gsb-approval-unique]"),
    approvalTotal: document.querySelector("[data-gsb-approval-total]"),
    approvalRate: document.querySelector("[data-gsb-approval-rate]"),
    approvalPrice: document.querySelector("[data-gsb-approval-price]"),
    approvalWarnings: document.querySelector("[data-gsb-approval-warnings]"),
    approvalWarningsList: document.querySelector("[data-gsb-approval-warnings-list]"),
    approvalCheckbox: document.querySelector("[data-gsb-approval-checkbox]"),
    approvalContinue: document.querySelector("[data-gsb-approval-continue]"),
    approvalCancels: document.querySelectorAll("[data-gsb-approval-cancel]"),
    approvalScroll: document.querySelector("[data-gsb-approval-scroll]"),
    approvalScrollHint: document.querySelector("[data-gsb-approval-scroll-hint]"),
    approvalActionsStatus: document.querySelector("[data-gsb-approval-actions-status]"),
    // Mode toggle: Upload files ↔ Names & Numbers
    modeToggleBtns: root.querySelectorAll("[data-gsb-mode]"),
    modePanels: root.querySelectorAll("[data-gsb-mode-panel]"),
    // Names & Numbers UI
    nnStyleTabs: root.querySelectorAll("[data-gsb-nn-style]"),
    nnPanels: root.querySelectorAll("[data-gsb-nn-panel]"),
    nnColourSwatches: root.querySelectorAll("[data-gsb-nn-colour]"),
    nnColourCustom: root.querySelector("[data-gsb-nn-colour-custom]"),
    nnSportsSize: root.querySelector("[data-gsb-nn-sports-size]"),
    nnFontButtons: root.querySelectorAll("[data-gsb-nn-font]"),
    nnCustomColourSwatches: root.querySelectorAll("[data-gsb-nn-custom-colour]"),
    nnCustomColourPicker: root.querySelector("[data-gsb-nn-custom-colour-picker]"),
    nnCustomHeight: root.querySelector("[data-gsb-nn-custom-height]"),
    nnRowList: root.querySelector("[data-gsb-nn-row-list]"),
    nnRowTemplate: document.querySelector("[data-gsb-nn-row-template]"),
    nnAddRow: root.querySelector("[data-gsb-nn-add-row]"),
    nnAddAll: root.querySelector("[data-gsb-nn-add-all]"),
    nnCount: root.querySelector("[data-gsb-nn-count]"),
    nnPasteOpen: root.querySelector("[data-gsb-nn-paste-open]"),
    nnPasteModal: document.querySelector("[data-gsb-nn-paste-modal]"),
    nnPasteInput: document.querySelector("[data-gsb-nn-paste-input]"),
    nnPasteCount: document.querySelector("[data-gsb-nn-paste-count]"),
    nnPasteImport: document.querySelector("[data-gsb-nn-paste-import]"),
    nnPasteCancels: document.querySelectorAll("[data-gsb-nn-paste-cancel]"),
    // Clean-background modal
    cleanBgModal: document.querySelector("[data-gsb-cleanbg-modal]"),
    cleanBgTitle: document.querySelector("[data-gsb-cleanbg-title]"),
    cleanBgBefore: document.querySelector("[data-gsb-cleanbg-before]"),
    cleanBgAfter: document.querySelector("[data-gsb-cleanbg-after]"),
    cleanBgStats: document.querySelector("[data-gsb-cleanbg-stats]"),
    cleanBgModeTabs: document.querySelectorAll("[data-gsb-cleanbg-mode]"),
    cleanBgThreshold: document.querySelector("[data-gsb-cleanbg-threshold]"),
    cleanBgThresholdValue: document.querySelector("[data-gsb-cleanbg-threshold-value]"),
    cleanBgFeather: document.querySelector("[data-gsb-cleanbg-feather]"),
    cleanBgFeatherValue: document.querySelector("[data-gsb-cleanbg-feather-value]"),
    cleanBgApply: document.querySelector("[data-gsb-cleanbg-apply]"),
    cleanBgCancels: document.querySelectorAll("[data-gsb-cleanbg-cancel]"),
  };

  // ============= Utils =============
  function makeId() {
    return "d_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);
  }

  function formatAud(amount) {
    return "$" + amount.toFixed(2);
  }

  /**
   * Multiply an ex-GST amount by the GST factor when the section is configured
   * to display inclusive prices. Returns { display, suffix } — the amount and
   * a text suffix ("inc. GST" or "+ GST") for the UI.
   */
  function grossPrice(exGstAmount) {
    if (CFG.showGstInclusive && CFG.gstPercent > 0) {
      return {
        amount: exGstAmount * (1 + CFG.gstPercent / 100),
        suffix: " inc. GST",
      };
    }
    return {
      amount: exGstAmount,
      suffix: " + GST",
    };
  }

  function formatDisplayPrice(exGstAmount) {
    const g = grossPrice(exGstAmount);
    return formatAud(g.amount) + g.suffix;
  }

  function formatDisplayRate(exGstRatePerCm, tierLabel) {
    // For per-cm rate display, the "$0.28/cm" text: if inclusive, gross it up.
    const g = grossPrice(exGstRatePerCm);
    return "$" + g.amount.toFixed(2) + "/cm" + (tierLabel ? " (tier " + tierLabel + ")" : "");
  }

  /**
   * Rough luminance check — used to pick a legible border colour for the
   * preview canvas depending on whether the customer picked a light or dark
   * shirt colour. Not colorimetrically perfect, just a quick check.
   */
  function isLightColor(hex) {
    if (!hex || hex[0] !== "#" || hex.length !== 7) return true;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    // ITU-R BT.601 luma coefficients — good enough for a border-colour flip.
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    return luma > 140;
  }

  /**
   * Update the "active" ring on the swatches to match the current previewBg.
   * If the current colour isn't one of the preset swatches, all swatches go
   * un-active (custom colour is in play — nothing to highlight).
   */
  function syncSwatches() {
    if (!el.bgSwatches || !el.bgSwatches.length) return;
    let matched = false;
    for (let i = 0; i < el.bgSwatches.length; i++) {
      const sw = el.bgSwatches[i];
      const isMatch = (sw.getAttribute("data-bg") || "").toLowerCase() === previewBg.toLowerCase();
      sw.classList.toggle("is-active", isMatch);
      sw.setAttribute("aria-checked", isMatch ? "true" : "false");
      if (isMatch) matched = true;
    }
    if (el.bgCustomInput) {
      // Keep the colour input in sync so the picker opens on the current bg.
      el.bgCustomInput.value = previewBg;
    }
    return matched;
  }

  /**
   * Update the current preview background colour, persist it, sync the
   * swatch UI, and re-render the canvas. Called by both swatch clicks and
   * the custom colour input.
   */
  function setPreviewBg(hex) {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
    previewBg = hex.toLowerCase();
    try { localStorage.setItem(BG_STORAGE_KEY, previewBg); } catch (_) {}
    syncSwatches();
    // Only redraw — don't repack (background doesn't affect layout at all).
    const valid = designs.filter(function (d) { return d.status === "ok" || d.status === "uploading"; });
    if (valid.length) drawCanvas(valid);
  }

  function showUploadError(message) {
    if (!el.uploadError) return;
    el.uploadError.textContent = message;
    el.uploadError.hidden = false;
    setTimeout(function () { el.uploadError.hidden = true; }, 6000);
  }

  function showCartError(message) {
    if (!el.cartError) return;
    if (!message) { el.cartError.hidden = true; el.cartError.textContent = ""; return; }
    el.cartError.textContent = message;
    el.cartError.hidden = false;
  }

  /**
   * Decode a file, find the bounding box of non-transparent (or non-white
   * for JPEG) pixels, and return the CROPPED image + its pixel dimensions.
   * The customer's file gets trimmed to only its visible content so we
   * don't pack empty pixels onto the gang sheet.
   *
   * Returns:
   *   originalWidthPx / originalHeightPx — before cropping
   *   croppedWidthPx / croppedHeightPx   — after trimming empty edges
   *   croppedDataUrl — the trimmed image as a data URL for preview + packing
   *   croppedBlob    — the trimmed image as a Blob for uploading
   *   trimmed        — true if we actually cropped anything off
   */
  function readImageAndCrop(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onload = function () {
        const img = new Image();
        img.onload = function () {
          try {
            let originalWidthPx = img.naturalWidth;
            let originalHeightPx = img.naturalHeight;

            // Customer uploads can be far higher resolution than any DTF
            // print actually needs (master files, unedited camera/scan
            // output). Decoding + getImageData on the raw upload with no
            // cap here reliably crashes the tab on large source files —
            // independent of gang sheet length, which is a separate cap
            // already applied in drawCanvas/buildCompositePng. Downscale
            // before touching pixels if the source exceeds the same safe
            // bounds those use: ~120 MP total, 16384 px/side. Genuine
            // uploads never get near this; it's a safety valve, not a
            // quality tradeoff anyone will notice.
            const MAX_SOURCE_AREA_PX = 120 * 1000 * 1000;
            const MAX_SOURCE_DIM_PX = 16384;
            if (originalWidthPx * originalHeightPx > MAX_SOURCE_AREA_PX || originalWidthPx > MAX_SOURCE_DIM_PX || originalHeightPx > MAX_SOURCE_DIM_PX) {
              const areaScale = Math.sqrt(MAX_SOURCE_AREA_PX / (originalWidthPx * originalHeightPx));
              const dimScale = Math.min(MAX_SOURCE_DIM_PX / originalWidthPx, MAX_SOURCE_DIM_PX / originalHeightPx);
              const downscale = Math.min(areaScale, dimScale, 1);
              originalWidthPx = Math.max(1, Math.round(originalWidthPx * downscale));
              originalHeightPx = Math.max(1, Math.round(originalHeightPx * downscale));
            }

            // Draw the full image to an off-screen canvas so we can read pixels.
            const c = document.createElement("canvas");
            c.width = originalWidthPx;
            c.height = originalHeightPx;
            const ctx = c.getContext("2d");
            if (!ctx) throw new Error("Canvas not supported.");
            ctx.drawImage(img, 0, 0, originalWidthPx, originalHeightPx);
            // Read pixel data + find bounding box of visible content.
            // Also collect DTF-specific print-readiness stats in the same
            // pixel walk (no extra CPU cost).
            let bbox;
            let printStats = null;
            try {
              const data = ctx.getImageData(0, 0, originalWidthPx, originalHeightPx).data;
              bbox = findContentBoundingBox(data, originalWidthPx, originalHeightPx, file.type);
              printStats = analyzeDtfPrintReadiness(data, originalWidthPx, originalHeightPx, file.type, bbox);
            } catch (e) {
              // getImageData can throw on some browsers if the image is tainted
              // (shouldn't happen since we're loading from a local File, but
              // just in case fall back to using the whole image untrimmed).
              bbox = { x: 0, y: 0, w: originalWidthPx, h: originalHeightPx };
            }
            // Render just the crop region onto a fresh canvas.
            const cw = Math.max(1, bbox.w);
            const ch = Math.max(1, bbox.h);
            const cropCanvas = document.createElement("canvas");
            cropCanvas.width = cw;
            cropCanvas.height = ch;
            const cropCtx = cropCanvas.getContext("2d");
            cropCtx.drawImage(c, bbox.x, bbox.y, cw, ch, 0, 0, cw, ch);
            const croppedDataUrl = cropCanvas.toDataURL(
              file.type === "image/jpeg" ? "image/jpeg" : "image/png",
              0.92
            );
            cropCanvas.toBlob(function (blob) {
              resolve({
                originalWidthPx: originalWidthPx,
                originalHeightPx: originalHeightPx,
                croppedWidthPx: cw,
                croppedHeightPx: ch,
                croppedDataUrl: croppedDataUrl,
                croppedBlob: blob,
                trimmed: cw !== originalWidthPx || ch !== originalHeightPx,
                printStats: printStats,
              });
            }, file.type === "image/jpeg" ? "image/jpeg" : "image/png", 0.92);
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = function () { reject(new Error("Couldn't decode image")); };
        img.src = reader.result;
      };
      reader.onerror = function () { reject(new Error("Couldn't read file")); };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Walk the pixel array and find the bounding box of "content" pixels.
   * For PNG/WebP: content = alpha > threshold. For JPEG: content = not pure
   * white (since JPEG has no alpha; customer's white background collapses to
   * exact white). If no content found, returns the full image bounds.
   */
  function findContentBoundingBox(data, width, height, mimeType) {
    const isJpeg = mimeType === "image/jpeg" || mimeType === "image/jpg";
    const ALPHA_THRESHOLD = 12; // 4.7% — kills stray anti-alias fringe pixels
    const WHITE_MARGIN = 245;   // JPEG: pixels above this on all channels are treated as background
    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        let isContent;
        if (isJpeg) {
          // JPEG background = near-white
          isContent = !(r >= WHITE_MARGIN && g >= WHITE_MARGIN && b >= WHITE_MARGIN);
        } else {
          isContent = a > ALPHA_THRESHOLD;
        }
        if (isContent) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0 || maxY < 0) {
      // No content pixels found — return the full image so we don't crash
      return { x: 0, y: 0, w: width, h: height };
    }
    return {
      x: minX,
      y: minY,
      w: (maxX - minX) + 1,
      h: (maxY - minY) + 1,
    };
  }

  /**
   * DTF-specific print-readiness analysis. Walks the content bbox once and
   * collects stats we can turn into actionable warnings. These are the checks
   * that matter for DTF specifically — competitors mostly stop at "300 DPI
   * recommended" and miss the ones that actually cause reprints.
   *
   * Returns:
   *   contentPx           — count of "content" pixels (non-transparent for PNG,
   *                         non-white for JPEG)
   *   semiTransparentRatio — fraction of pixels with alpha 1..254 (PNG only).
   *                         High values mean soft/anti-aliased edges that
   *                         cause visible fringe on DTF prints.
   *   nearBlackRatio      — fraction of pixels that are near-black (all
   *                         channels <15). Heavy black coverage saturates the
   *                         print head and can bleed on smaller designs.
   *   solidnessRatio      — average number of content neighbours (4-conn)
   *                         per content pixel, normalised 0..1. High values
   *                         mean solid shapes (e.g. filled logos). Low values
   *                         mean thin strokes / spidery designs, which are
   *                         where DTF powder adhesion fails.
   *   edgeRatio           — fraction of content pixels that touch a
   *                         non-content pixel. High edge ratio = complex
   *                         boundary or lots of small parts.
   */
  function analyzeDtfPrintReadiness(data, width, height, mimeType, bbox) {
    const isJpeg = mimeType === "image/jpeg" || mimeType === "image/jpg";
    const ALPHA_THRESHOLD = 12;
    const WHITE_MARGIN = 245;
    const NEAR_BLACK = 15;

    let contentPx = 0;
    let semiTransparentPx = 0;
    let nearBlackPx = 0;
    let contentNeighborSum = 0; // total 4-conn content neighbours across all content pixels
    let edgeContentPx = 0;      // content pixels with at least one non-content neighbour

    function isContentAt(x, y) {
      if (x < 0 || y < 0 || x >= width || y >= height) return false;
      const i = (y * width + x) * 4;
      if (isJpeg) {
        return !(data[i] >= WHITE_MARGIN && data[i + 1] >= WHITE_MARGIN && data[i + 2] >= WHITE_MARGIN);
      }
      return data[i + 3] > ALPHA_THRESHOLD;
    }

    const x0 = Math.max(0, bbox.x);
    const y0 = Math.max(0, bbox.y);
    const x1 = Math.min(width, bbox.x + bbox.w);
    const y1 = Math.min(height, bbox.y + bbox.h);

    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const i = (y * width + x) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];

        let content;
        if (isJpeg) {
          content = !(r >= WHITE_MARGIN && g >= WHITE_MARGIN && b >= WHITE_MARGIN);
        } else {
          content = a > ALPHA_THRESHOLD;
          // Only count semi-transparent when it's meaningful content (a > 0
          // but not fully opaque). Fully-transparent pixels aren't "soft
          // edges" — they're background.
          if (a > 0 && a < 255) semiTransparentPx++;
        }

        if (!content) continue;
        contentPx++;

        if (r < NEAR_BLACK && g < NEAR_BLACK && b < NEAR_BLACK && a > 128) nearBlackPx++;

        // 4-connectivity content neighbour count (fastest useful shape metric)
        let n = 0;
        let isEdge = false;
        if (isContentAt(x - 1, y)) n++; else isEdge = true;
        if (isContentAt(x + 1, y)) n++; else isEdge = true;
        if (isContentAt(x, y - 1)) n++; else isEdge = true;
        if (isContentAt(x, y + 1)) n++; else isEdge = true;
        contentNeighborSum += n;
        if (isEdge) edgeContentPx++;
      }
    }

    if (contentPx === 0) {
      return {
        contentPx: 0,
        semiTransparentRatio: 0,
        nearBlackRatio: 0,
        solidnessRatio: 1,
        edgeRatio: 0,
      };
    }

    return {
      contentPx: contentPx,
      semiTransparentRatio: semiTransparentPx / contentPx,
      nearBlackRatio: nearBlackPx / contentPx,
      solidnessRatio: contentNeighborSum / (contentPx * 4), // 0..1
      edgeRatio: edgeContentPx / contentPx,
    };
  }

  /** Convert pixels → cm using the configured DPI. */
  function pxToCm(px) {
    return (px / CFG.designDpi) * CM_PER_INCH;
  }

  /**
   * Inspect the raw file bytes to detect colour mode + bit depth. Runs before
   * the file is decoded so we can warn about problem inputs (indexed palette,
   * greyscale, CMYK JPEG). We do NOT convert colour spaces client-side —
   * browsers can't emit CMYK, and doing bad RGB→CMYK conversion in JS is
   * worse than letting your DTF RIP handle the conversion at print time with
   * calibrated ICC profiles. Detection just informs the customer + staff.
   *
   * Returns { format, mode, bitDepth, hasAlpha }.
   *   format: 'png' | 'jpeg' | 'webp' | 'unknown'
   *   mode:   'rgb' | 'rgba' | 'grayscale' | 'grayscale-alpha' | 'indexed' | 'cmyk' | 'unknown'
   *   bitDepth: bits per channel
   *   hasAlpha: bool
   */
  async function detectColorMode(file) {
    try {
      const slice = file.slice(0, 8192);
      const buf = await slice.arrayBuffer();
      const bytes = new Uint8Array(buf);

      // PNG — first chunk after 8-byte signature is IHDR.
      if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
        // IHDR data begins at byte 16: width(4), height(4), bitDepth(1), colorType(1)
        const bitDepth = bytes[24];
        const colorType = bytes[25];
        const table = { 0: "grayscale", 2: "rgb", 3: "indexed", 4: "grayscale-alpha", 6: "rgba" };
        const mode = table[colorType] || "unknown";
        return {
          format: "png",
          mode: mode,
          bitDepth: bitDepth,
          hasAlpha: colorType === 4 || colorType === 6,
        };
      }

      // JPEG — scan markers for SOF (start of frame). APP14 gives Adobe
      // colour transform which distinguishes CMYK from YCCK.
      if (bytes[0] === 0xFF && bytes[1] === 0xD8) {
        let i = 2;
        let adobeTransform = -1;
        while (i < bytes.length - 4) {
          if (bytes[i] !== 0xFF) { i++; continue; }
          const marker = bytes[i + 1];
          if (marker === 0xD8 || marker === 0xD9 || marker === 0x00) { i += 2; continue; }
          // APP14 "Adobe" marker
          if (marker === 0xEE && i + 14 < bytes.length) {
            const isAdobe =
              bytes[i + 4] === 0x41 && bytes[i + 5] === 0x64 && bytes[i + 6] === 0x6F &&
              bytes[i + 7] === 0x62 && bytes[i + 8] === 0x65;
            if (isAdobe) adobeTransform = bytes[i + 13]; // 0 = unknown (RGB/CMYK), 1 = YCbCr, 2 = YCCK
          }
          // SOF markers: 0xC0..C3, C5..C7, C9..CB, CD..CF (excluding C4/C8/CC)
          const isSof =
            (marker >= 0xC0 && marker <= 0xC3) ||
            (marker >= 0xC5 && marker <= 0xC7) ||
            (marker >= 0xC9 && marker <= 0xCB) ||
            (marker >= 0xCD && marker <= 0xCF);
          if (isSof) {
            const bitDepth = bytes[i + 4];
            const components = bytes[i + 9];
            let mode = "unknown";
            if (components === 1) mode = "grayscale";
            else if (components === 3) mode = "rgb"; // functionally sRGB for print purposes
            else if (components === 4) mode = "cmyk"; // 4-component JPEG is always CMYK/YCCK
            return { format: "jpeg", mode: mode, bitDepth: bitDepth, hasAlpha: false };
          }
          // Skip segment: length is 2 bytes big-endian starting at i+2
          const length = (bytes[i + 2] << 8) | bytes[i + 3];
          if (length < 2) { i += 2; continue; }
          i += 2 + length;
        }
        return { format: "jpeg", mode: "rgb", bitDepth: 8, hasAlpha: false };
      }

      // WebP — RIFF...WEBP header. Most WebPs are RGBA-compatible; we treat
      // them uniformly here.
      if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
          bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
        return { format: "webp", mode: "rgba", bitDepth: 8, hasAlpha: true };
      }

      return { format: "unknown", mode: "unknown", bitDepth: 8, hasAlpha: false };
    } catch (e) {
      return { format: "unknown", mode: "unknown", bitDepth: 8, hasAlpha: false };
    }
  }

  /**
   * Given a design's colour mode + resolution + chosen print size, return an
   * array of {level, text} warnings to show to the customer. Called both when
   * a file is first uploaded and whenever the customer resizes it (a big
   * upscale can push effective DPI below print-quality).
   */
  function buildDesignWarnings(design) {
    const warnings = [];
    const cm = design.colorMode || {};

    // Colour mode issues
    if (cm.mode === "indexed") {
      warnings.push({
        level: "error",
        text: "Indexed colour palette detected — DTF prints of palette PNGs show visible banding. Re-export as full RGB PNG with transparency.",
      });
    } else if (cm.mode === "grayscale" || cm.mode === "grayscale-alpha") {
      warnings.push({
        level: "warn",
        text: "Greyscale image — will print in shades of black only. Confirm this is intentional.",
      });
    } else if (cm.mode === "cmyk") {
      warnings.push({
        level: "info",
        text: "CMYK JPEG detected — your RIP will honour these colour values directly.",
      });
    }

    if (cm.bitDepth && cm.bitDepth < 8) {
      warnings.push({
        level: "warn",
        text: "Low colour depth (" + cm.bitDepth + "-bit) — may show colour banding.",
      });
    }

    // JPEG with no transparency — DTF prints white ink where the image has
    // non-transparent pixels, so any white background will print as white ink
    // on the film.
    if (cm.format === "jpeg" && !cm.hasAlpha) {
      warnings.push({
        level: "info",
        text: "JPEG has no transparent background — any white in the file will print as white ink. Use a PNG with transparency if you want the shirt colour to show through.",
      });
    }

    // Effective resolution vs chosen print size. Native DPI = pixels / inches.
    // Warn if under 200 DPI at the chosen print size (below print-quality).
    if (design.croppedWidthPx > 0 && design.widthCm > 0) {
      const inchesWide = design.widthCm / CM_PER_INCH;
      const effectiveDpi = design.croppedWidthPx / inchesWide;
      if (effectiveDpi < 150) {
        warnings.push({
          level: "error",
          text: "Effective resolution " + Math.round(effectiveDpi) + " DPI at chosen print size — too low for a crisp print. Reduce the print size or supply a higher-res file.",
        });
      } else if (effectiveDpi < 220) {
        warnings.push({
          level: "warn",
          text: "Effective resolution " + Math.round(effectiveDpi) + " DPI at chosen print size — soft edges likely. 300 DPI is recommended for DTF.",
        });
      }
    }

    // DTF-specific print-readiness checks (from the pixel-walk stats we
    // collected during upload). These are the ones competitors miss.
    const ps = design.printStats;
    if (ps && ps.contentPx > 0) {
      // Soft / semi-transparent edges — cause visible fringe on DTF prints.
      // Only checked for PNG/WebP; JPEG has no alpha channel to analyse.
      if (cm.format !== "jpeg" && ps.semiTransparentRatio > 0.25) {
        warnings.push({
          level: "warn",
          text: "Soft edges detected (" + Math.round(ps.semiTransparentRatio * 100) + "% partially-transparent pixels). DTF prints these with visible fringing. Flatten to solid alpha (all-or-nothing) for the cleanest print.",
        });
      } else if (cm.format !== "jpeg" && ps.semiTransparentRatio > 0.12) {
        warnings.push({
          level: "info",
          text: "Some soft edges — you may see slight fringing on the print. Flatten the alpha channel if you want the crispest edges.",
        });
      }

      // Thin strokes / spidery designs — DTF powder can't adhere to strokes
      // narrower than ~0.3mm. Solidness ratio < 0.5 means most content
      // pixels have fewer than 2/4 content neighbours, which is characteristic
      // of thin lines and text.
      // Convert the effective print size into an "average feature size in mm"
      // proxy so we only warn when the thinness matters at print scale.
      const minPrintDimCm = Math.min(design.widthCm || 0, design.heightCm || 0);
      if (ps.solidnessRatio < 0.55 && minPrintDimCm > 0 && minPrintDimCm < 30) {
        warnings.push({
          level: "warn",
          text: "Thin strokes or fine detail detected. DTF powder needs ~0.3mm minimum stroke width to hold — very fine lines may drop out on the transfer. Consider thickening lines or increasing the print size.",
        });
      }

      // Heavy near-black coverage — clogs print heads on some printers, and
      // very saturated black can bleed slightly on small designs.
      if (ps.nearBlackRatio > 0.45) {
        warnings.push({
          level: "info",
          text: "Heavy black coverage (" + Math.round(ps.nearBlackRatio * 100) + "% near-black). Prints fine, but slightly softer edges on very fine text. If you want max sharpness, try rich black (small amounts of C/M/Y under the black).",
        });
      }
    }

    // Small final print — some detail simply won't transfer well below ~2cm.
    if (design.widthCm > 0 && design.heightCm > 0) {
      const minDim = Math.min(design.widthCm, design.heightCm);
      if (minDim < 1.5) {
        warnings.push({
          level: "warn",
          text: "Very small print (" + minDim.toFixed(1) + "cm on the short side). Fine detail and thin strokes may not transfer cleanly at this size — consider bumping up.",
        });
      }
    }

    return warnings;
  }

  function renderDesignWarnings(design) {
    if (!design._rowEl) return;
    const container = design._rowEl.querySelector("[data-gsb-warnings]");
    if (!container) return;
    const warnings = buildDesignWarnings(design);
    container.innerHTML = "";
    container.hidden = warnings.length === 0;
    for (let i = 0; i < warnings.length; i++) {
      const w = warnings[i];
      const div = document.createElement("div");
      div.className = "gsb__design-warning gsb__design-warning--" + w.level;
      div.textContent = w.text;
      container.appendChild(div);
    }
  }

  // ============= Names & Numbers (text builder) =============
  // Sports preset uses one fixed font (Anton — jersey-style block) and
  // three predefined sizes for adult / kids / patch. Custom mode gives the
  // customer a full font grid + free height + colour picker.
  const NN_SPORTS_SIZES = {
    adult: { nameMm: 80,  numberMm: 250 },
    kids:  { nameMm: 60,  numberMm: 200 },
    patch: { nameMm: 40,  numberMm: 120 },
  };
  const NN_SPORTS_FONT = "Anton";
  const NN_STACK_GAP_MM = 6; // space between name and number in stacked layout

  // Live state for the N&N form
  const nnState = {
    mode: "upload",   // 'upload' | 'names'
    style: "sports",  // 'sports' | 'custom'
    // Sports
    sportsColour: "#ffffff",
    sportsSize: "adult",
    // Custom
    customFont: "Anton",
    customColour: "#000000",
    customHeightMm: 60,
    // Rows
    rows: [], // [{ id, name, number }]
  };

  // Small helper — sanitize the label used for the generated filename.
  function sanitizeFilename(text) {
    return (text || "text")
      .replace(/[^a-zA-Z0-9 _-]/g, "")
      .replace(/\s+/g, "-")
      .toLowerCase()
      .slice(0, 40) || "text";
  }

  // ============= Text rendering pipeline =============
  /**
   * Wait for a specific font at a given size to be loaded before rendering
   * with it. Guards against the fallback font kicking in on first render.
   */
  async function ensureFontLoaded(font, fontSize) {
    if (!document.fonts || !document.fonts.load) return;
    try {
      await document.fonts.load('bold ' + Math.round(fontSize) + 'px "' + font + '"');
      // Also wait for the general fonts.ready in case the CSS is still loading
      if (document.fonts.ready && document.fonts.status !== "loaded") {
        await document.fonts.ready;
      }
    } catch (_) { /* fall back silently to system font */ }
  }

  /**
   * Render a single line of text to a transparent-background PNG blob at
   * the target DPI (typically 300). Uses the browser's actualBoundingBox
   * metrics (which report the real glyph extents including flourishes,
   * descenders, and italic overhang) rather than the advance-width alone,
   * so decorative and script fonts don't get clipped on the edges.
   *
   * @param {string} text
   * @param {string} font       Font family name
   * @param {string} colour     CSS colour (#hex)
   * @param {number} heightMm   Target visible cap height in mm
   * @param {number} dpi
   * @returns {Promise<Blob>}
   */
  async function renderTextPng(text, font, colour, heightMm, dpi) {
    // The Names & Numbers custom-height field's HTML max="500" only
    // constrains the spinner arrows, not typed or pasted values — a
    // customer can get an arbitrarily large heightMm here with no browser
    // validation stopping them. Clamp at the point the canvas actually
    // gets sized, same as every other canvas op in this file, rather than
    // trusting the input's own max attribute to hold.
    heightMm = Math.min(500, Math.max(1, heightMm));
    const heightPx = Math.max(60, Math.round((heightMm / 25.4) * dpi));
    // Font-size sized so cap-height ≈ requested visible height (~72% of em).
    let fontSize = Math.round(heightPx / 0.72);
    await ensureFontLoaded(font, fontSize);

    const measureC = document.createElement("canvas");
    const mctx = measureC.getContext("2d");

    // Measure with the actual glyph-bounds metrics at a given font size.
    // These include:
    //   - Any part of the glyph that extends left of the origin
    //   - Any part that extends right of the advance width
    //   - The actual top (ascent) and bottom (descent) of the drawn ink
    // Older browsers may not populate all fields — we fall back gracefully.
    function measureAt(size) {
      const fontString = 'bold ' + size + 'px "' + font + '", "Impact", sans-serif';
      mctx.font = fontString;
      mctx.textBaseline = "alphabetic";
      const metrics = mctx.measureText(text);
      const actualLeft = isFinite(metrics.actualBoundingBoxLeft) ? metrics.actualBoundingBoxLeft : 0;
      const actualRight = isFinite(metrics.actualBoundingBoxRight) ? metrics.actualBoundingBoxRight : (metrics.width || size);
      const actualAscent = isFinite(metrics.actualBoundingBoxAscent) ? metrics.actualBoundingBoxAscent : size * 0.78;
      const actualDescent = isFinite(metrics.actualBoundingBoxDescent) ? metrics.actualBoundingBoxDescent : size * 0.28;
      // A small pad protects against sub-pixel rounding + font-hinting artifacts.
      const padding = Math.max(4, Math.round(size * 0.06));
      return {
        fontString: fontString,
        actualLeft: actualLeft, actualAscent: actualAscent, padding: padding,
        widthPx: Math.max(60, Math.ceil(actualLeft + actualRight + padding * 2)),
        canvasHeight: Math.max(60, Math.ceil(actualAscent + actualDescent + padding * 2)),
      };
    }

    let m = measureAt(fontSize);

    // heightMm alone doesn't bound canvas WIDTH — a long string (up to the
    // row inputs' own 20-char name / 4-char number limits) at even the
    // clamped 500mm height can still produce a canvas past the browser's
    // ~16384px per-side / ~120MP total limits (a bare 4-digit number at
    // 500mm is already ~18000px wide). Shrink the font and re-measure once
    // if the natural size would exceed those bounds.
    const MAX_DIM_PX = 16384;
    const MAX_AREA_PX = 120 * 1000 * 1000;
    if (m.widthPx > MAX_DIM_PX || m.canvasHeight > MAX_DIM_PX || m.widthPx * m.canvasHeight > MAX_AREA_PX) {
      const dimScale = Math.min(MAX_DIM_PX / m.widthPx, MAX_DIM_PX / m.canvasHeight);
      const areaScale = Math.sqrt(MAX_AREA_PX / (m.widthPx * m.canvasHeight));
      fontSize = Math.max(8, Math.round(fontSize * Math.min(dimScale, areaScale, 1)));
      m = measureAt(fontSize);
    }

    const { fontString, actualLeft, actualAscent, padding, widthPx, canvasHeight } = m;

    const c = document.createElement("canvas");
    c.width = widthPx;
    c.height = canvasHeight;
    const ctx = c.getContext("2d");
    ctx.font = fontString;
    ctx.fillStyle = colour;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    // Position the text so that the actual glyph bounds sit inside the
    // canvas with `padding` on every side. Fully inside → no clipping.
    const drawX = padding + actualLeft;
    const drawY = padding + actualAscent;
    ctx.fillText(text, drawX, drawY);

    return await new Promise(function (res) { c.toBlob(res, "image/png"); });
  }

  /** Load a Blob into an HTMLImageElement via object URL. */
  function blobToImage(blob) {
    return new Promise(function (res, rej) {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); res(img); };
      img.onerror = function () { URL.revokeObjectURL(url); rej(new Error("image load failed")); };
      img.src = url;
    });
  }

  /**
   * Render name above number with a small vertical gap between them.
   * Both texts share the same font + colour. Used by both the sports
   * preset and custom mode (when the customer fills in both fields).
   */
  async function renderStackedPng(name, number, font, colour, nameHeightMm, numberHeightMm, dpi) {
    const nameBlob = await renderTextPng(name, font, colour, nameHeightMm, dpi);
    const numberBlob = await renderTextPng(number, font, colour, numberHeightMm, dpi);
    const nameImg = await blobToImage(nameBlob);
    const numberImg = await blobToImage(numberBlob);
    const gapPx = Math.round((NN_STACK_GAP_MM / 25.4) * dpi);
    const widthPx = Math.max(nameImg.naturalWidth, numberImg.naturalWidth);
    const heightPx = nameImg.naturalHeight + gapPx + numberImg.naturalHeight;
    const c = document.createElement("canvas");
    c.width = widthPx;
    c.height = heightPx;
    const ctx = c.getContext("2d");
    ctx.drawImage(nameImg, Math.round((widthPx - nameImg.naturalWidth) / 2), 0);
    ctx.drawImage(numberImg, Math.round((widthPx - numberImg.naturalWidth) / 2), nameImg.naturalHeight + gapPx);
    return await new Promise(function (res) { c.toBlob(res, "image/png"); });
  }

  /**
   * Given a single row + the current N&N settings, produce the PNG blob for
   * that row's design. Handles: sports (always stacked), custom (stacked if
   * both fields filled, single line if only one).
   */
  async function renderNnRow(row) {
    const isSports = nnState.style === "sports";
    const font = isSports ? NN_SPORTS_FONT : nnState.customFont;
    const colour = isSports ? nnState.sportsColour : nnState.customColour;
    const dpi = CFG.designDpi;
    const name = (row.name || "").trim();
    const number = (row.number || "").trim();

    if (isSports) {
      const s = NN_SPORTS_SIZES[nnState.sportsSize] || NN_SPORTS_SIZES.adult;
      if (name && number) return renderStackedPng(name, number, font, colour, s.nameMm, s.numberMm, dpi);
      if (name) return renderTextPng(name, font, colour, s.nameMm, dpi);
      if (number) return renderTextPng(number, font, colour, s.numberMm, dpi);
    } else {
      const h = Math.max(10, nnState.customHeightMm);
      // Custom mode: use custom height for the taller (number) element.
      // Name height defaults to 40% of number height for a jersey-style
      // proportion. If only one field is filled, render at h directly.
      const nameH = h * 0.4;
      const numberH = h;
      if (name && number) return renderStackedPng(name, number, font, colour, nameH, numberH, dpi);
      if (name) return renderTextPng(name, font, colour, h, dpi);
      if (number) return renderTextPng(number, font, colour, h, dpi);
    }
    return null;
  }

  // ============= Row management =============
  function makeNnRowId() { return "nn_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 6); }

  function addNnRow(name, number) {
    const row = { id: makeNnRowId(), name: name || "", number: number || "" };
    nnState.rows.push(row);
    renderNnRowUI(row);
    updateNnCount();
  }

  function renderNnRowUI(row) {
    if (!el.nnRowTemplate || !el.nnRowList) return;
    const li = el.nnRowTemplate.content.firstElementChild.cloneNode(true);
    li.setAttribute("data-gsb-nn-row-id", row.id);
    const nameInput = li.querySelector("[data-gsb-nn-row-name]");
    const numberInput = li.querySelector("[data-gsb-nn-row-number]");
    const removeBtn = li.querySelector("[data-gsb-nn-row-remove]");
    if (nameInput) {
      nameInput.value = row.name;
      nameInput.addEventListener("input", function () {
        row.name = nameInput.value;
        updateNnCount();
      });
    }
    if (numberInput) {
      numberInput.value = row.number;
      numberInput.addEventListener("input", function () {
        row.number = numberInput.value;
        updateNnCount();
      });
    }
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        const idx = nnState.rows.findIndex(function (r) { return r.id === row.id; });
        if (idx >= 0) nnState.rows.splice(idx, 1);
        li.remove();
        updateNnCount();
      });
    }
    el.nnRowList.appendChild(li);
  }

  function updateNnCount() {
    const valid = nnState.rows.filter(function (r) {
      return (r.name && r.name.trim()) || (r.number && r.number.trim());
    });
    if (el.nnCount) el.nnCount.textContent = valid.length + " design" + (valid.length === 1 ? "" : "s") + " will be added";
    if (el.nnAddAll) el.nnAddAll.disabled = valid.length === 0;
    // Persist so N&N rows survive a page reload
    if (typeof scheduleSessionSave === "function") scheduleSessionSave();
  }

  function clearNnRows() {
    nnState.rows = [];
    if (el.nnRowList) el.nnRowList.innerHTML = "";
    updateNnCount();
  }

  // ============= CSV parsing =============
  /**
   * Parse a pasted table of team roster data. Handles tab, comma, or
   * multi-space separators. Numeric-only tokens are treated as jersey
   * numbers; everything else is treated as name. If a line has one
   * number and one name (in either order), we assign each correctly.
   */
  function parseNnPaste(text) {
    if (!text) return [];
    const lines = text.split(/\r?\n/).map(function (l) { return l.trim(); }).filter(function (l) { return l.length > 0; });
    return lines.map(function (line) {
      let parts;
      if (line.indexOf("\t") >= 0) parts = line.split("\t");
      else if (line.indexOf(",") >= 0) parts = line.split(",");
      else parts = line.split(/\s{2,}|\s+(?=\S)/);
      parts = parts.map(function (p) { return p.trim(); }).filter(function (p) { return p.length > 0; });

      let name = "", number = "";
      const numericParts = [];
      const nameParts = [];
      for (let i = 0; i < parts.length; i++) {
        if (/^\d{1,4}$/.test(parts[i])) numericParts.push(parts[i]);
        else nameParts.push(parts[i]);
      }
      name = nameParts.join(" ").trim();
      number = numericParts[0] || "";
      return { name: name, number: number };
    }).filter(function (r) { return r.name || r.number; });
  }

  // ============= Add all rows to sheet =============
  /**
   * Iterate the current N&N rows, render each to a PNG blob, and feed it
   * through the existing handleFile pipeline (which cropping, colour-mode
   * detection, print-stat analysis, upload, packing all handle for us).
   */
  async function addAllNnToSheet() {
    if (!el.nnAddAll) return;
    const valid = nnState.rows.filter(function (r) {
      return (r.name && r.name.trim()) || (r.number && r.number.trim());
    });
    if (!valid.length) return;

    el.nnAddAll.disabled = true;
    const btnLabel = el.nnAddAll.querySelector("span");
    const origLabel = btnLabel ? btnLabel.textContent : "Add all to sheet";

    for (let i = 0; i < valid.length; i++) {
      const row = valid[i];
      if (btnLabel) btnLabel.textContent = "Building " + (i + 1) + " of " + valid.length + "…";
      try {
        const blob = await renderNnRow(row);
        if (!blob) continue;
        const labelName = [row.name, row.number].filter(Boolean).join("-");
        const filename = sanitizeFilename(labelName) + ".png";
        const file = new File([blob], filename, { type: "image/png" });
        await handleFile(file, { skipRender: true });
        // N&N text designs have a definite "up" direction — lock rotation on
        // the design we just created so the packer doesn't lay names sideways.
        const created = designs[designs.length - 1];
        if (created) created.allowRotation = false;
      } catch (e) {
        // Skip this row and continue — better than aborting the whole batch
      }
    }
    repackAndRender();

    // Reset UI + clear rows so the customer can add more
    clearNnRows();
    el.nnAddAll.disabled = true;
    if (btnLabel) btnLabel.textContent = origLabel;
    // Auto-switch back to Upload panel so the customer sees their designs land
    setMode("upload");
  }

  // ============= Mode + style toggles =============
  function setMode(mode) {
    nnState.mode = mode;
    if (el.modeToggleBtns) {
      el.modeToggleBtns.forEach(function (btn) {
        btn.classList.toggle("is-active", btn.getAttribute("data-gsb-mode") === mode);
      });
    }
    if (el.modePanels) {
      el.modePanels.forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-gsb-mode-panel") !== mode;
      });
    }
    // Ensure at least one N&N row exists when switching in
    if (mode === "names" && nnState.rows.length === 0) {
      addNnRow("", "");
    }
    if (typeof scheduleSessionSave === "function") scheduleSessionSave();
  }

  function setNnStyle(style) {
    nnState.style = style;
    if (el.nnStyleTabs) {
      el.nnStyleTabs.forEach(function (tab) {
        tab.classList.toggle("is-active", tab.getAttribute("data-gsb-nn-style") === style);
      });
    }
    if (el.nnPanels) {
      el.nnPanels.forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-gsb-nn-panel") !== style;
      });
    }
    if (typeof scheduleSessionSave === "function") scheduleSessionSave();
  }

  // ============= Clean background tool =============
  // State for the currently-open clean-background modal.
  let cleanBgState = null; // { design, mode, threshold, feather, srcImageData }

  /**
   * Walk pixel data and convert near-black (or near-white) pixels to
   * transparent. Distance from the target background colour is measured as:
   *   - Black mode: max(R,G,B) — 0 for pure black, higher for any colour
   *   - White mode: 255 - min(R,G,B) — 0 for pure white
   * Pixels within `threshold` of the target become fully transparent. Pixels
   * within `threshold + feather` get proportional alpha for a smooth edge.
   */
  function removeBackgroundPixels(imageData, mode, threshold, feather) {
    const src = imageData.data;
    const out = new Uint8ClampedArray(src);
    const isBlack = mode === "black";
    for (let i = 0; i < out.length; i += 4) {
      const r = out[i], g = out[i + 1], b = out[i + 2], a = out[i + 3];
      if (a === 0) continue; // already transparent
      const distance = isBlack ? Math.max(r, g, b) : (255 - Math.min(r, g, b));
      if (distance < threshold) {
        out[i + 3] = 0;
      } else if (feather > 0 && distance < threshold + feather) {
        const t = (distance - threshold) / feather;
        out[i + 3] = Math.round(a * t);
      }
    }
    return new ImageData(out, imageData.width, imageData.height);
  }

  /**
   * Open the clean-background modal for a specific design. Loads the design's
   * current cropped pixels into memory (once), populates the "Before" image,
   * kicks off the initial "After" render, and shows the modal.
   */
  async function openCleanBgModal(design) {
    if (!el.cleanBgModal) return;

    cleanBgState = {
      design: design,
      mode: "black",
      threshold: 30,
      feather: 10,
      srcImageData: null,
    };

    // Reset slider + tab state
    if (el.cleanBgThreshold) el.cleanBgThreshold.value = String(cleanBgState.threshold);
    if (el.cleanBgThresholdValue) el.cleanBgThresholdValue.textContent = String(cleanBgState.threshold);
    if (el.cleanBgFeather) el.cleanBgFeather.value = String(cleanBgState.feather);
    if (el.cleanBgFeatherValue) el.cleanBgFeatherValue.textContent = String(cleanBgState.feather);
    if (el.cleanBgModeTabs) {
      el.cleanBgModeTabs.forEach(function (tab) {
        tab.classList.toggle("is-active", tab.getAttribute("data-gsb-cleanbg-mode") === "black");
      });
    }

    if (el.cleanBgTitle) el.cleanBgTitle.textContent = "Clean background — " + design.file.name;
    if (el.cleanBgBefore) el.cleanBgBefore.src = design.previewDataUrl || "";
    if (el.cleanBgStats) el.cleanBgStats.textContent = "";

    // Preload the design's cropped pixels into a canvas → ImageData.
    // We decode from previewDataUrl (which is the already-cropped version).
    try {
      const img = await new Promise(function (res, rej) {
        const im = new Image();
        im.onload = function () { res(im); };
        im.onerror = function () { rej(new Error("Couldn't decode design for background removal.")); };
        im.src = design.previewDataUrl;
      });
      const tmpC = document.createElement("canvas");
      tmpC.width = img.naturalWidth;
      tmpC.height = img.naturalHeight;
      const tmpCtx = tmpC.getContext("2d");
      tmpCtx.drawImage(img, 0, 0);
      cleanBgState.srcImageData = tmpCtx.getImageData(0, 0, tmpC.width, tmpC.height);
    } catch (e) {
      showUploadError("Couldn't open background cleaner: " + e.message);
      return;
    }

    // Initial render + show
    renderCleanBgAfter();
    el.cleanBgModal.hidden = false;
    el.cleanBgModal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }

  function closeCleanBgModal() {
    if (!el.cleanBgModal) return;
    el.cleanBgModal.hidden = true;
    el.cleanBgModal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    cleanBgState = null;
  }

  /**
   * Render the "After" canvas by applying the current settings to the cached
   * source ImageData. Cheap enough to run live on every slider change for
   * typical design sizes; if you throw a 5000×5000 file at it, this may
   * briefly hitch — that's acceptable.
   */
  function renderCleanBgAfter() {
    if (!cleanBgState || !cleanBgState.srcImageData) return;
    const src = cleanBgState.srcImageData;
    const cleaned = removeBackgroundPixels(src, cleanBgState.mode, cleanBgState.threshold, cleanBgState.feather);

    const canvas = el.cleanBgAfter;
    if (!canvas) return;
    canvas.width = src.width;
    canvas.height = src.height;
    canvas.getContext("2d").putImageData(cleaned, 0, 0);

    // Count how many pixels went from opaque to transparent
    let removedPx = 0, contentPx = 0;
    const srcData = src.data;
    const cleanedData = cleaned.data;
    for (let i = 0; i < cleanedData.length; i += 4) {
      if (srcData[i + 3] > 0) contentPx++;
      if (cleanedData[i + 3] === 0 && srcData[i + 3] !== 0) removedPx++;
    }
    if (el.cleanBgStats) {
      const pct = contentPx > 0 ? ((removedPx / contentPx) * 100).toFixed(1) : "0";
      el.cleanBgStats.textContent = "Removed " + pct + "% of the design's pixels";
    }
  }

  /**
   * Apply the current settings to the actual design object. Re-crops the
   * cleaned pixels, updates all derived design properties, re-uploads the
   * cleaned file to R2 in the background, and closes the modal.
   */
  async function applyCleanBg() {
    if (!cleanBgState || !cleanBgState.srcImageData) return;
    const state = cleanBgState;
    const design = state.design;

    // Apply removal
    const cleaned = removeBackgroundPixels(
      state.srcImageData, state.mode, state.threshold, state.feather
    );

    // Put onto a canvas
    const c = document.createElement("canvas");
    c.width = cleaned.width;
    c.height = cleaned.height;
    c.getContext("2d").putImageData(cleaned, 0, 0);

    // Re-crop to the new content bbox (removing bg shrinks the visible area)
    const bbox = findContentBoundingBox(cleaned.data, cleaned.width, cleaned.height, "image/png");
    const cw = Math.max(1, bbox.w);
    const ch = Math.max(1, bbox.h);
    const croppedC = document.createElement("canvas");
    croppedC.width = cw;
    croppedC.height = ch;
    croppedC.getContext("2d").drawImage(c, bbox.x, bbox.y, cw, ch, 0, 0, cw, ch);

    // Encode
    const dataUrl = croppedC.toDataURL("image/png");
    const blob = await new Promise(function (res) { croppedC.toBlob(res, "image/png"); });

    // Read cleaned + cropped pixels for new print stats
    const finalCtx = croppedC.getContext("2d");
    let finalStats = null;
    try {
      const finalData = finalCtx.getImageData(0, 0, cw, ch).data;
      finalStats = analyzeDtfPrintReadiness(finalData, cw, ch, "image/png", { x: 0, y: 0, w: cw, h: ch });
    } catch (_) { /* non-fatal */ }

    // Update the design object
    design.previewDataUrl = dataUrl;
    design.croppedWidthPx = cw;
    design.croppedHeightPx = ch;
    // Recompute print size at the configured DPI, preserving customer's
    // ability to override in the size inputs
    const newWidthCm = pxToCm(cw);
    const newHeightCm = pxToCm(ch);
    design.widthCm = newWidthCm;
    design.heightCm = newHeightCm;
    design.detectedWidthCm = newWidthCm;
    design.detectedHeightCm = newHeightCm;
    design.aspectRatio = cw / ch;
    design.trimmed = true;
    design._imgCache = null;   // force canvas to re-load
    design._imgLoading = false;
    if (finalStats) design.printStats = finalStats;
    // Colour mode is now definitely RGBA with alpha
    design.colorMode = { format: "png", mode: "rgba", bitDepth: 8, hasAlpha: true };

    // Refresh the row UI (thumb, size inputs, warnings)
    if (design._rowEl) {
      const thumb = design._rowEl.querySelector("[data-gsb-thumb]");
      if (thumb) thumb.src = dataUrl;
      const nameEl = design._rowEl.querySelector("[data-gsb-name]");
      if (nameEl) nameEl.textContent = design.file.name + " (background cleaned)";
      const wIn = design._rowEl.querySelector("[data-gsb-w-mm]");
      const hIn = design._rowEl.querySelector("[data-gsb-h-mm]");
      if (wIn) wIn.value = String(Math.round(design.widthCm * 10));
      if (hIn) hIn.value = String(Math.round(design.heightCm * 10));
      const resetBtn = design._rowEl.querySelector("[data-gsb-reset-size]");
      if (resetBtn) resetBtn.hidden = true;
      renderDesignWarnings(design);
    }

    // Prepare the cleaned file for re-upload
    const originalName = design.file.name.replace(/\.(png|jpe?g|webp)$/i, "");
    const cleanedFile = new File([blob], originalName + "-cleaned.png", { type: "image/png" });
    design.uploadFile = cleanedFile;
    design.uploadedUrl = null;
    design.status = "uploading";
    if (design._rowEl) updateRowStatus(design._rowEl, design);

    // Close modal immediately — repack + re-render right away so the customer
    // sees the cleaned design on the sheet, while the upload runs in the bg.
    closeCleanBgModal();
    repackAndRender();

    try {
      const url = await uploadDesignFile(cleanedFile);
      design.uploadedUrl = url;
      design.status = "ok";
    } catch (e) {
      design.status = "error";
      design.errorMessage = "Re-upload of cleaned file failed";
    }
    if (design._rowEl) updateRowStatus(design._rowEl, design);
    repackAndRender();
  }

  // Upload a file to the worker; returns the stored URL
  async function uploadDesignFile(file) {
    if (!CFG.workerUrl) {
      throw new Error("Uploader isn't configured — worker URL missing.");
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", "gang-sheet-design");
    const r = await fetch(CFG.workerUrl + "/api/customer-upload", {
      method: "POST",
      body: formData,
    });
    if (!r.ok) {
      let body = null;
      try { body = await r.json(); } catch (_) {}
      throw new Error((body && body.error) || ("Upload failed (" + r.status + ")"));
    }
    const data = await r.json();
    if (!data.url) throw new Error("Upload succeeded but no URL returned.");
    return data.url;
  }

  // ============= Packing algorithm =============
  /**
   * MaxRects bin packing (best-area-fit + bottom-left tiebreak).
   *
   * The sheet is fixed-width and effectively unbounded in length. We track a
   * set of "free rectangles" — subregions of the sheet where new items can
   * still be placed. For each item (sorted by area descending) we pick the
   * free rect that fits it with the smallest wasted area, place the item at
   * its top-left, then split any free rects that overlap the placed item's
   * footprint into up to four smaller rects. Contained rects get pruned.
   *
   * Why this over the previous shelf packer: shelf-packing wastes the
   * vertical space beside tall items (e.g. a 28cm circle would create a
   * 28cm-tall shelf, and small items placed next to it would leave big empty
   * gaps below them). MaxRects backfills those gaps automatically because it
   * keeps the "empty space under a small item next to a tall item" as a
   * usable free rect for future items.
   *
   * Item footprints are inflated by `gap` on the right/bottom to naturally
   * enforce spacing between adjacent items. The initial free rect is inset
   * by `gap` on all sides to give a border around the sheet.
   *
   * @param {Array<{id: string, sourceId: string, widthMm: number, heightMm: number}>} items
   * @returns {{placed: Array, totalLengthMm: number}}
   */
  function shelfPack(items) {
    const gap = CFG.gapMm;
    const usableW = SHEET_WIDTH_MM - 2 * gap;
    const HUGE = 1e6; // effectively unbounded sheet length while packing

    if (!items.length) return { placed: [], totalLengthMm: 0 };

    // Sort by area descending — big items first typically gives the best
    // MaxRects results because small items backfill leftover pockets.
    const sorted = items.slice().sort(function (a, b) {
      return (b.widthMm * b.heightMm) - (a.widthMm * a.heightMm);
    });

    /** @type {Array<{x:number,y:number,w:number,h:number}>} */
    const freeRects = [{ x: gap, y: gap, w: usableW, h: HUGE }];
    const placed = [];
    let maxYUsed = 0;

    /**
     * Try placing an item in either orientation and pick whichever fits with
     * the least wasted area. If the item's allowRotation flag is false, we
     * only try the original orientation. Returns { fr, rotated } or null.
     */
    function tryPlace(item) {
      let bestFr = null;
      let bestWaste = Infinity;
      let bestY = Infinity;
      let bestX = Infinity;
      let bestRotated = false;

      const orientations = item.allowRotation === false ? [false] : [false, true];

      for (let o = 0; o < orientations.length; o++) {
        const rotated = orientations[o];
        // When rotated, swap width and height for the packing footprint
        const effW = rotated ? item.heightMm : item.widthMm;
        const effH = rotated ? item.widthMm : item.heightMm;
        if (effW > usableW) continue; // this orientation doesn't fit at all

        // We check the item at its REAL size — the initial free rect and
        // the split step already reserve the gap zones on all sides, so
        // adding gap to iw/ih would double-count and reject items that
        // would fit fine (e.g. 275mm wide item in a 277mm free rect).
        const iw = effW;
        const ih = effH;

        for (let i = 0; i < freeRects.length; i++) {
          const fr = freeRects[i];
          if (fr.w + 1e-6 < iw || fr.h + 1e-6 < ih) continue;
          // Score by wasted area — use the reserved footprint (with gap)
          // so ties break in favour of tighter placements against neighbours.
          const rw = effW + gap;
          const rh = effH + gap;
          const waste = (fr.w * fr.h) - (rw * rh);
          const isBetter = waste < bestWaste - 1e-6;
          const isTie = Math.abs(waste - bestWaste) < 1e-6;
          const tieBreak = isTie && (fr.y < bestY || (fr.y === bestY && fr.x < bestX));
          if (isBetter || tieBreak) {
            bestFr = fr;
            bestWaste = waste;
            bestY = fr.y;
            bestX = fr.x;
            bestRotated = rotated;
          }
        }
      }
      return bestFr ? { fr: bestFr, rotated: bestRotated } : null;
    }

    function splitAllOverlapping(uX1, uY1, uX2, uY2) {
      const next = [];
      for (let i = 0; i < freeRects.length; i++) {
        const fr = freeRects[i];
        const fx1 = fr.x, fy1 = fr.y, fx2 = fr.x + fr.w, fy2 = fr.y + fr.h;
        // No overlap → keep as-is
        if (uX1 >= fx2 || uX2 <= fx1 || uY1 >= fy2 || uY2 <= fy1) {
          next.push(fr);
          continue;
        }
        // Overlaps — split into up to 4 leftover strips
        if (uY1 > fy1) next.push({ x: fx1, y: fy1, w: fr.w, h: uY1 - fy1 });          // top strip
        if (uY2 < fy2) next.push({ x: fx1, y: uY2, w: fr.w, h: fy2 - uY2 });          // bottom strip
        if (uX1 > fx1) next.push({ x: fx1, y: fy1, w: uX1 - fx1, h: fr.h });          // left strip
        if (uX2 < fx2) next.push({ x: uX2, y: fy1, w: fx2 - uX2, h: fr.h });          // right strip
      }
      // Prune: remove any rect fully contained in another (dedupe overlap).
      for (let i = 0; i < next.length; i++) {
        for (let j = 0; j < next.length; j++) {
          if (i === j) continue;
          const a = next[i], b = next[j];
          if (a.x >= b.x && a.y >= b.y && (a.x + a.w) <= (b.x + b.w) && (a.y + a.h) <= (b.y + b.h)) {
            next.splice(i, 1); i--; break;
          }
        }
      }
      freeRects.length = 0;
      for (let i = 0; i < next.length; i++) freeRects.push(next[i]);
    }

    for (let i = 0; i < sorted.length; i++) {
      const item = sorted[i];
      const placement = tryPlace(item);
      if (!placement) continue; // couldn't place (item too big for the sheet)

      const fr = placement.fr;
      const rotated = placement.rotated;
      // Effective placement dimensions after rotation
      const effW = rotated ? item.heightMm : item.widthMm;
      const effH = rotated ? item.widthMm : item.heightMm;

      const px = fr.x;
      const py = fr.y;
      placed.push({
        id: item.id,
        sourceId: item.sourceId, // preserve link back to source design for canvas draw
        xMm: px,
        yMm: py,
        widthMm: effW,
        heightMm: effH,
        rotated: rotated, // packer chose to rotate this item 90° for better fit
      });
      const bottom = py + effH;
      if (bottom > maxYUsed) maxYUsed = bottom;

      // The item's reserved footprint (item + right/bottom gap) is what free
      // rects need to be split around. This gives natural spacing between
      // adjacent items.
      splitAllOverlapping(px, py, px + effW + gap, py + effH + gap);
    }

    const totalLengthMm = maxYUsed + gap;
    return { placed: placed, totalLengthMm: totalLengthMm };
  }

  // ============= Pricing =============
  /**
   * Which tier does a given length fall into? Returns { tier: 1|2|3, rate }.
   * The whole length is priced at the winning tier's rate.
   */
  function tierForLength(lengthCm) {
    if (lengthCm <= CFG.tier1MaxCm) return { tier: 1, rate: CFG.tier1Rate };
    if (lengthCm <= CFG.tier2MaxCm) return { tier: 2, rate: CFG.tier2Rate };
    return { tier: 3, rate: CFG.tier3Rate };
  }

  function computePrice(lengthMm) {
    if (!lengthMm) return { price: 0, tier: 1, rate: CFG.tier1Rate };
    const lengthCm = lengthMm / MM_PER_CM;
    const tierInfo = tierForLength(lengthCm);
    const raw = lengthCm * tierInfo.rate;
    return {
      price: Math.max(CFG.minChargeAud, raw),
      tier: tierInfo.tier,
      rate: tierInfo.rate,
      hitMin: raw < CFG.minChargeAud,
    };
  }

  // ============= Rendering =============
  function repackAndRender() {
    // Persist the current state so a page reload / tab close doesn't lose
    // work. Debounced 400ms in scheduleSessionSave.
    if (typeof scheduleSessionSave === "function") scheduleSessionSave();
    // Filter out designs still uploading or errored
    const valid = designs.filter(function (d) { return d.status === "ok" || d.status === "uploading"; });
    // Multiply each design out by its quantity — the packer treats each copy
    // as its own rect. Copies share the source design id (via `sourceId`) so
    // the canvas draw can look up the thumbnail.
    const items = [];
    valid.forEach(function (d) {
      const qty = Math.max(1, d.quantity || 1);
      for (let i = 0; i < qty; i++) {
        items.push({
          id: d.id + "#" + (i + 1),
          sourceId: d.id,
          widthMm: d.widthCm * MM_PER_CM,
          heightMm: d.heightCm * MM_PER_CM,
          // Design can be rotated 90° by the packer if it saves space.
          // N&N-generated designs and any customer-locked designs opt out.
          allowRotation: d.allowRotation !== false,
        });
      }
    });
    layout = shelfPack(items);

    // Compute price + active tier
    const lengthCm = layout.totalLengthMm / MM_PER_CM;
    const priced = computePrice(layout.totalLengthMm);

    // Show/hide preview panels
    if (valid.length === 0) {
      el.previewEmpty.hidden = false;
      el.previewInner.hidden = true;
    } else {
      el.previewEmpty.hidden = true;
      el.previewInner.hidden = false;
      drawCanvas(valid);
    }

    // Update summary. Count = total copies across all designs (i.e. the
    // total number of transfers on the sheet).
    const totalCopies = valid.reduce(function (s, d) { return s + Math.max(1, d.quantity || 1); }, 0);
    el.lengthLabel.textContent = lengthCm > 0 ? lengthCm.toFixed(1) + " cm" : "—";
    el.countLabel.textContent = valid.length === 0 ? "0"
      : totalCopies + " (" + valid.length + " unique)";
    if (el.rateLabel) {
      el.rateLabel.textContent = lengthCm > 0
        ? formatDisplayRate(priced.rate, priced.tier)
        : "—";
    }
    el.priceLabel.textContent = formatDisplayPrice(priced.price);

    // Highlight the active tier row in the pricing table
    if (el.tierRows && el.tierRows.length) {
      el.tierRows.forEach(function (row) {
        const t = parseInt(row.getAttribute("data-gsb-tier"), 10);
        if (lengthCm > 0 && t === priced.tier) row.classList.add("is-active");
        else row.classList.remove("is-active");
      });
    }

    // Add-to-cart validation
    const allOk = designs.length > 0 && designs.every(function (d) { return d.status === "ok"; });
    const withinLength = lengthCm >= CFG.minSheetLengthCm && lengthCm <= CFG.maxSheetLengthCm;
    if (allOk && withinLength && valid.length > 0 && CFG.variantId) {
      el.addToCartBtn.disabled = false;
      showCartError("");
    } else {
      el.addToCartBtn.disabled = true;
      if (lengthCm > CFG.maxSheetLengthCm) {
        showCartError("Your layout is longer than the max sheet length (" + CFG.maxSheetLengthCm + " cm). Remove or shrink some designs.");
      } else if (!CFG.variantId && designs.length > 0) {
        showCartError("This section isn't wired to a Shopify product yet. Roland: set the variant ID in the theme editor.");
      } else {
        showCartError("");
      }
    }
  }

  // Draw the packed layout onto the preview canvas. Renders:
  //   1. Ruler gutters along top + left with cm tick marks and labels
  //   2. Customer-picked background colour on the sheet area (preview only —
  //      the printed composite stays transparent)
  //   3. Every placed design at its actual packed position and size
  //   4. Sheet border so roll edges are obvious
  //   5. Updates the external stats bar (dimensions + packing efficiency)
  function drawCanvas(validDesigns) {
    const canvas = el.canvas;
    const ctx = canvas.getContext("2d");

    // Ruler geometry — reserve a fixed gutter on top + left, in CSS pixels.
    // Everything in this function that's measured in "CSS pixels" is applied
    // as-is; DPR scaling is handled via ctx.setTransform below.
    const RULER_TOP_PX = 22;
    const RULER_LEFT_PX = 26;
    const DEVICE_PIXEL_RATIO = Math.min(3, window.devicePixelRatio || 1);

    // This canvas re-renders on every edit while the customer is actively
    // arranging designs — unlike the one-shot export paths (see
    // buildCompositePng, which already caps itself the same way), an
    // unbounded 2 px/mm zoom here means a long sheet's *actual pixel
    // buffer* grows without limit and reliably crashes the tab well before
    // checkout, not just at export time. Back the zoom off automatically
    // for long sheets so the buffer never exceeds the same empirically-
    // safe caps buildCompositePng uses (~120 MP total, 16384 px/side) —
    // customers just see a more zoomed-out preview, nothing else changes.
    const MAX_CANVAS_AREA_PX = 120 * 1000 * 1000;
    const MAX_CANVAS_DIM_PX = 16384;
    let scale = 2; // preview pixels per mm at logical CSS size, default zoom
    while (scale > 0.02) {
      const wPx = (SHEET_WIDTH_MM * scale + RULER_LEFT_PX) * DEVICE_PIXEL_RATIO;
      const hPx = (Math.max(layout.totalLengthMm * scale, 100) + RULER_TOP_PX) * DEVICE_PIXEL_RATIO;
      if (wPx <= MAX_CANVAS_DIM_PX && hPx <= MAX_CANVAS_DIM_PX && wPx * hPx <= MAX_CANVAS_AREA_PX) break;
      scale *= 0.9;
    }

    const sheetWidthCss = SHEET_WIDTH_MM * scale;
    const sheetHeightCss = Math.max(layout.totalLengthMm * scale, 100);

    // Full canvas including the ruler gutters
    const canvasWidthCss = sheetWidthCss + RULER_LEFT_PX;
    const canvasHeightCss = sheetHeightCss + RULER_TOP_PX;

    canvas.width = canvasWidthCss * DEVICE_PIXEL_RATIO;
    canvas.height = canvasHeightCss * DEVICE_PIXEL_RATIO;
    canvas.style.width = ""; // let CSS fill container
    canvas.style.height = "auto"; // CSS width is 100% (from the frame)
    ctx.setTransform(DEVICE_PIXEL_RATIO, 0, 0, DEVICE_PIXEL_RATIO, 0, 0);

    // Clear (transparent so the frame's border colour bleeds through).
    ctx.clearRect(0, 0, canvasWidthCss, canvasHeightCss);

    // ---- Sheet body (offset by ruler gutter) ----
    const sheetOx = RULER_LEFT_PX;
    const sheetOy = RULER_TOP_PX;

    // Background — customer's chosen preview colour. NOT written to composite.
    ctx.fillStyle = previewBg;
    ctx.fillRect(sheetOx, sheetOy, sheetWidthCss, sheetHeightCss);

    // Sheet border — adapts for contrast against light or dark preview bg
    const isLightBg = isLightColor(previewBg);
    const sheetBorder = isLightBg ? "rgba(0,0,0,0.22)" : "rgba(255,255,255,0.28)";
    const rulerText = isLightBg ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.8)";
    const rulerLine = isLightBg ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.4)";
    const rulerFaint = isLightBg ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.18)";

    ctx.strokeStyle = sheetBorder;
    ctx.lineWidth = 1;
    ctx.strokeRect(sheetOx + 0.5, sheetOy + 0.5, sheetWidthCss - 1, sheetHeightCss - 1);

    // ---- Rulers ----
    ctx.font = "600 10px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = rulerText;
    ctx.textBaseline = "middle";

    const sheetWidthCm = CFG.sheetWidthCm;
    const sheetLengthCm = layout.totalLengthMm / MM_PER_CM;

    // Ruler background (very subtle so it doesn't fight the sheet)
    ctx.fillStyle = isLightBg ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)";
    ctx.fillRect(0, 0, canvasWidthCss, RULER_TOP_PX);
    ctx.fillRect(0, 0, RULER_LEFT_PX, canvasHeightCss);

    // Top ruler — X axis (cm across the sheet width)
    ctx.textAlign = "center";
    for (let cm = 0; cm <= Math.ceil(sheetWidthCm); cm++) {
      const px = sheetOx + (cm * 10 * scale);
      if (px > sheetOx + sheetWidthCss) break;
      const isMajor = cm % 5 === 0;
      ctx.strokeStyle = isMajor ? rulerLine : rulerFaint;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px + 0.5, RULER_TOP_PX - (isMajor ? 8 : 4));
      ctx.lineTo(px + 0.5, RULER_TOP_PX);
      ctx.stroke();
      if (isMajor) {
        ctx.fillStyle = rulerText;
        ctx.fillText(cm + "", px, RULER_TOP_PX - 14);
      }
    }

    // Left ruler — Y axis (cm down the sheet length)
    ctx.textAlign = "right";
    const maxLenCm = Math.ceil(sheetLengthCm);
    // Choose label interval so we don't overcrowd the ruler on long sheets
    let labelEvery = 5;
    if (maxLenCm > 150) labelEvery = 25;
    else if (maxLenCm > 80) labelEvery = 10;
    for (let cm = 0; cm <= maxLenCm; cm++) {
      const py = sheetOy + (cm * 10 * scale);
      if (py > sheetOy + sheetHeightCss) break;
      const isMajor = cm % labelEvery === 0;
      ctx.strokeStyle = isMajor ? rulerLine : rulerFaint;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(RULER_LEFT_PX - (isMajor ? 8 : 4), py + 0.5);
      ctx.lineTo(RULER_LEFT_PX, py + 0.5);
      ctx.stroke();
      if (isMajor && cm > 0) {
        ctx.fillStyle = rulerText;
        ctx.fillText(cm + "", RULER_LEFT_PX - 10, py);
      }
    }

    // "cm" label in the corner (small, low-key)
    ctx.textAlign = "center";
    ctx.fillStyle = rulerText;
    ctx.font = "700 9px system-ui, -apple-system, sans-serif";
    ctx.fillText("cm", RULER_LEFT_PX / 2, RULER_TOP_PX / 2);

    // ---- Designs ----
    const byId = {};
    validDesigns.forEach(function (d) { byId[d.id] = d; });

    // Kick off image loads for any designs that don't have a cache yet.
    validDesigns.forEach(function (d) {
      if (d.previewDataUrl && !d._imgCache && !d._imgLoading) {
        d._imgLoading = true;
        const img = new Image();
        img.onload = function () {
          d._imgCache = img;
          d._imgLoading = false;
          drawCanvas(validDesigns);
        };
        img.onerror = function () { d._imgLoading = false; };
        img.src = d.previewDataUrl;
      }
    });

    layout.placed.forEach(function (p) {
      const d = byId[p.sourceId] || byId[p.id];
      if (!d) return;
      const x = sheetOx + p.xMm * scale;
      const y = sheetOy + p.yMm * scale;
      const w = p.widthMm * scale;
      const h = p.heightMm * scale;
      if (!d._imgCache) {
        ctx.fillStyle = "rgba(255,107,0,0.08)";
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = "#ff6b00";
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, w, h);
      } else {
        try {
          if (p.rotated) {
            // Draw the image rotated 90° clockwise so its natural width
            // (which is now the placed rect's HEIGHT) runs top-to-bottom.
            ctx.save();
            ctx.translate(x + w, y);
            ctx.rotate(Math.PI / 2);
            // After rotation, drawImage(_, 0, 0, w, h) draws at rotated size
            ctx.drawImage(d._imgCache, 0, 0, h, w);
            ctx.restore();
          } else {
            ctx.drawImage(d._imgCache, x, y, w, h);
          }
        } catch (_) {
          ctx.fillStyle = "rgba(255,107,0,0.08)";
          ctx.fillRect(x, y, w, h);
        }
      }
      // Small rotation indicator in the corner so customers see which
      // designs the packer chose to rotate for a tighter fit.
      if (p.rotated) {
        const badge = 14;
        ctx.fillStyle = "rgba(255,107,0,0.85)";
        ctx.fillRect(x + w - badge - 2, y + 2, badge, badge);
        ctx.fillStyle = "#fff";
        ctx.font = "700 10px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("⟳", x + w - badge / 2 - 2, y + 2 + badge / 2);
      }
    });

    // ---- Stats bar (external DOM elements above the canvas) ----
    if (el.canvasDimensions) {
      el.canvasDimensions.textContent =
        sheetWidthCm + " × " + sheetLengthCm.toFixed(1) + " cm";
    }
    if (el.canvasEfficiency) {
      // Efficiency = sum of placed design area ÷ total sheet area.
      // Communicates "you're getting good value out of the paid length."
      let placedAreaMm2 = 0;
      layout.placed.forEach(function (p) {
        placedAreaMm2 += p.widthMm * p.heightMm;
      });
      const sheetAreaMm2 = SHEET_WIDTH_MM * layout.totalLengthMm;
      const pct = sheetAreaMm2 > 0 ? (placedAreaMm2 / sheetAreaMm2) * 100 : 0;
      el.canvasEfficiency.textContent = Math.round(pct) + "% used";
      // Colour-hint the number so 70%+ feels good and <40% feels sparse
      if (pct >= 70) el.canvasEfficiency.style.color = "#4ade80";
      else if (pct >= 40) el.canvasEfficiency.style.color = "#ffce66";
      else el.canvasEfficiency.style.color = "#ff9a6b";
    }
  }

  // ============= Design list rendering =============
  function renderDesignRow(design) {
    const template = el.designTemplate;
    if (!template) return null;
    const row = template.content.firstElementChild.cloneNode(true);
    row.setAttribute("data-gsb-design-id", design.id);
    const thumb = row.querySelector("[data-gsb-thumb]");
    const name = row.querySelector("[data-gsb-name]");
    const qtyInput = row.querySelector("[data-gsb-qty-input]");
    const wInput = row.querySelector("[data-gsb-w-mm]");
    const hInput = row.querySelector("[data-gsb-h-mm]");
    const resetBtn = row.querySelector("[data-gsb-reset-size]");
    const removeBtn = row.querySelector("[data-gsb-remove]");

    thumb.src = design.previewDataUrl;
    name.textContent = design.file.name + (design.trimmed ? " (trimmed)" : "");
    if (qtyInput) qtyInput.value = design.quantity || 1;

    // Fill the W/H inputs from the current print size, and show the reset
    // button only when the customer has changed the size from the auto-detected
    // value. Rounding to the nearest millimetre so the inputs stay integer-clean.
    function refreshSizeInputs() {
      if (wInput) wInput.value = String(Math.round(design.widthCm * 10));
      if (hInput) hInput.value = String(Math.round(design.heightCm * 10));
      const isCustom =
        Math.abs(design.widthCm - design.detectedWidthCm) > 0.05 ||
        Math.abs(design.heightCm - design.detectedHeightCm) > 0.05;
      if (resetBtn) resetBtn.hidden = !isCustom;
    }
    refreshSizeInputs();

    updateRowStatus(row, design);

    // Aspect ratio is locked to the source file's ratio — customers can pick
    // width or height, the other dimension follows. Prevents distortion.
    const MIN_MM = 5;
    const sheetMaxMm = SHEET_WIDTH_MM - 2 * CFG.gapMm;

    function applySize(sourceInput) {
      const raw = parseFloat(sourceInput.value);
      if (!isFinite(raw) || raw <= 0) return; // ignore mid-typing states
      let wMm, hMm;
      if (sourceInput === wInput) {
        wMm = Math.max(MIN_MM, raw);
        hMm = wMm / design.aspectRatio;
      } else {
        hMm = Math.max(MIN_MM, raw);
        wMm = hMm * design.aspectRatio;
      }
      // If width would exceed the usable sheet width, clamp width down (which
      // also shrinks height via the locked aspect).
      if (wMm > sheetMaxMm) {
        wMm = sheetMaxMm;
        hMm = wMm / design.aspectRatio;
        showUploadError('"' + design.file.name + '" — capped to sheet max width (' + Math.round(sheetMaxMm) + 'mm).');
      }
      design.widthCm = wMm / 10;
      design.heightCm = hMm / 10;
      refreshSizeInputs();
      renderDesignWarnings(design); // resolution check depends on print size
      repackAndRender();
    }
    if (wInput) wInput.addEventListener("change", function () { applySize(wInput); });
    if (hInput) hInput.addEventListener("change", function () { applySize(hInput); });

    if (resetBtn) {
      resetBtn.addEventListener("click", function () {
        design.widthCm = design.detectedWidthCm;
        design.heightCm = design.detectedHeightCm;
        refreshSizeInputs();
        renderDesignWarnings(design);
        repackAndRender();
      });
    }

    if (qtyInput) {
      qtyInput.addEventListener("input", function () {
        const q = Math.max(1, Math.min(1000, parseInt(qtyInput.value, 10) || 1));
        design.quantity = q;
        if (String(q) !== qtyInput.value) qtyInput.value = q;
        repackAndRender();
      });
    }

    removeBtn.addEventListener("click", function () {
      const idx = designs.findIndex(function (d) { return d.id === design.id; });
      if (idx >= 0) designs.splice(idx, 1);
      row.remove();
      if (designs.length === 0) el.designsStep.hidden = true;
      repackAndRender();
    });

    const cleanBgBtn = row.querySelector("[data-gsb-clean-bg]");
    if (cleanBgBtn) {
      cleanBgBtn.addEventListener("click", function () {
        openCleanBgModal(design);
      });
    }

    const rotationLockBtn = row.querySelector("[data-gsb-rotation-lock]");
    const rotationLockLabel = row.querySelector("[data-gsb-rotation-lock-label]");
    function refreshRotationLockUi() {
      const locked = design.allowRotation === false;
      if (rotationLockBtn) {
        rotationLockBtn.classList.toggle("is-locked", locked);
        rotationLockBtn.title = locked
          ? "Locked — this design won't be rotated. Click to allow auto-rotation."
          : "Auto-rotate is allowed. Click to lock this design to its original orientation.";
      }
      if (rotationLockLabel) rotationLockLabel.textContent = locked ? "Rotation: locked" : "Auto-rotate: on";
    }
    refreshRotationLockUi();
    if (rotationLockBtn) {
      rotationLockBtn.addEventListener("click", function () {
        design.allowRotation = design.allowRotation === false ? true : false;
        refreshRotationLockUi();
        repackAndRender();
      });
    }

    design._rowEl = row;
    renderDesignWarnings(design);
    return row;
  }

  function updateRowStatus(row, design) {
    const statusEl = row.querySelector("[data-gsb-status]");
    if (!statusEl) return;
    statusEl.classList.remove("is-ok", "is-err");
    if (design.status === "uploading") {
      statusEl.textContent = "Uploading…";
    } else if (design.status === "ok") {
      statusEl.textContent = "Ready ✓";
      statusEl.classList.add("is-ok");
    } else if (design.status === "error") {
      statusEl.textContent = design.errorMessage || "Upload failed";
      statusEl.classList.add("is-err");
    }
  }

  // ============= File handling =============
  // `opts.skipRender` lets bulk callers (handleFileList, addAllNnToSheet)
  // suppress the per-file repackAndRender() below and do exactly one at the
  // end instead. Without it, adding N files re-packs + redraws from scratch
  // after EVERY single one — repackAndRender's cost grows with the design
  // count already added, so N files costs O(N²) total instead of O(N).
  // Confirmed via testing: a 200-row Names & Numbers "Add all to sheet"
  // import visibly slowed down as it progressed (15 rows in 27s, then only
  // 23 more in the next 81s) and was still going after 3+ minutes — this is
  // almost certainly what customers doing real bulk team-roster orders were
  // hitting and reporting as the page crashing.
  async function handleFile(file, opts) {
    const skipRender = !!(opts && opts.skipRender);
    if (!ACCEPTED_TYPES.includes(file.type) && !/\.(png|jpe?g|webp)$/i.test(file.name)) {
      showUploadError('"' + file.name + '" — that file type isn\'t supported. Use PNG, JPG, or WEBP.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showUploadError('"' + file.name + '" is too big (' + Math.round(file.size / 1024 / 1024) + ' MB). Max is 25 MB.');
      return;
    }

    let cropInfo;
    try {
      cropInfo = await readImageAndCrop(file);
    } catch (e) {
      showUploadError('"' + file.name + '" — couldn\'t decode this image.');
      return;
    }

    // Detect colour mode in parallel so we can warn on problem files. Runs
    // off the raw bytes — doesn't need the decoded image.
    const colorMode = await detectColorMode(file);

    // Derive print dimensions from the CROPPED pixels at the assumed DPI.
    let widthCm = pxToCm(cropInfo.croppedWidthPx);
    let heightCm = pxToCm(cropInfo.croppedHeightPx);
    // Safety: if the design is wider than the sheet after crop, scale down
    // to fit and warn. Height scales proportionally.
    if (widthCm > CFG.sheetWidthCm - 2) {
      const scale = (CFG.sheetWidthCm - 2) / widthCm;
      widthCm *= scale;
      heightCm *= scale;
      showUploadError('"' + file.name + '" was wider than the sheet — scaled it down to fit.');
    }
    const aspectRatio = cropInfo.croppedWidthPx / cropInfo.croppedHeightPx;

    // Prefer to upload the CROPPED file (smaller, whitespace-trimmed) so
    // fulfilment gets the trimmed asset. Fall back to the original if
    // canvas.toBlob didn't succeed.
    const uploadFile = cropInfo.croppedBlob
      ? new File([cropInfo.croppedBlob], file.name.replace(/\.(png|jpe?g|webp)$/i, "-trimmed.$1"), { type: cropInfo.croppedBlob.type })
      : file;

    const design = {
      id: makeId(),
      file: file,
      uploadFile: uploadFile,
      originalWidthPx: cropInfo.originalWidthPx,
      originalHeightPx: cropInfo.originalHeightPx,
      croppedWidthPx: cropInfo.croppedWidthPx,
      croppedHeightPx: cropInfo.croppedHeightPx,
      trimmed: cropInfo.trimmed,
      widthCm: widthCm,
      heightCm: heightCm,
      // Remember the auto-detected dimensions so the reset button can restore
      // them after the customer changes W/H in the size inputs.
      detectedWidthCm: widthCm,
      detectedHeightCm: heightCm,
      aspectRatio: aspectRatio,
      quantity: 1,
      previewDataUrl: cropInfo.croppedDataUrl,
      colorMode: colorMode,
      printStats: cropInfo.printStats || null,
      uploadedUrl: null,
      status: "uploading",
    };
    designs.push(design);

    // Render the row + kick off upload
    el.designsStep.hidden = false;
    const row = renderDesignRow(design);
    if (row) el.designsList.appendChild(row);
    if (!skipRender) repackAndRender();

    try {
      const url = await uploadDesignFile(design.uploadFile || file);
      design.uploadedUrl = url;
      design.status = "ok";
    } catch (e) {
      design.status = "error";
      design.errorMessage = e.message || "Upload failed";
    }
    if (design._rowEl) updateRowStatus(design._rowEl, design);
    if (!skipRender) repackAndRender();
  }

  async function handleFileList(fileList) {
    if (!fileList || !fileList.length) return;
    // Snapshot into a real array FIRST. `fileList` is often a live
    // FileList tied to the <input> element (or a DataTransfer that's only
    // guaranteed valid for the synchronous event dispatch) — if the caller
    // resets `input.value = ""` (as the change handler below does, right
    // after calling this) while this loop is still awaiting an earlier
    // file, that reset clears the SAME live list this loop is iterating,
    // so `fileList.length` drops to 0 mid-loop and every file after the
    // first is silently skipped with no error. Copying to a plain array
    // up front decouples iteration from whatever happens to the input
    // afterward. Confirmed via testing: uploading 2 files at once only
    // processed the first until this fix.
    const files = Array.prototype.slice.call(fileList);
    for (let i = 0; i < files.length; i++) {
      await handleFile(files[i], { skipRender: true });
    }
    repackAndRender();
  }

  // ============= PNG DPI metadata injection =============
  //
  // canvas.toBlob() writes a valid PNG but omits the pHYs chunk (physical
  // pixel dimensions). RIP software then defaults to 72 DPI interpretation,
  // scaling a 300 DPI file to ~4× its intended physical size — Roland's
  // "wrong dimensions" bug.
  //
  // We fix it entirely client-side by parsing the PNG bytes, inserting a
  // pHYs chunk immediately after IHDR with pixels-per-meter derived from
  // the render DPI, and reassembling. pHYs is a well-defined PNG spec
  // chunk (see https://www.w3.org/TR/PNG/#11pHYs) so any conformant reader
  // picks it up — Photoshop, most RIPs (Onyx, Caldera, Wasatch), sharp,
  // ImageMagick etc.
  //
  // 9 bytes of chunk data:
  //   [0..3]  pixels per unit, X axis (big-endian uint32)
  //   [4..7]  pixels per unit, Y axis (big-endian uint32)
  //   [8]     unit specifier (1 = metre; 0 = unknown/aspect only)
  const _CRC_TABLE = (function () {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) {
        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      }
      t[n] = c >>> 0;
    }
    return t;
  })();
  function pngCrc32(bytes, start, end) {
    let crc = 0xFFFFFFFF;
    for (let i = start; i < end; i++) {
      crc = (_CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8)) >>> 0;
    }
    return (crc ^ 0xFFFFFFFF) >>> 0;
  }

  async function injectPhysChunk(blob, dpi) {
    if (!blob || !dpi || dpi <= 0) return blob;

    // Memory-efficient version. The original implementation read the ENTIRE
    // PNG into a Uint8Array (`await blob.arrayBuffer()`) and then allocated
    // a second Uint8Array for the output — that doubles peak memory usage
    // right at the point the tab is already juggling a huge canvas. For
    // 200MB composites on 902cm sheets, that pushed Chrome over its per-
    // tab memory limit and triggered "Error code 5" (STATUS_ACCESS_VIOLATION).
    //
    // Instead: read ONLY the first 33 bytes (PNG signature + IHDR) to
    // verify format, build the 21-byte pHYs chunk in memory, then stitch
    // the original blob back together using Blob slice + concat. The
    // browser handles the underlying byte streaming — no double buffer.

    const PNG_SIG = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
    if (blob.size < 33) return blob;

    // Only load the first 33 bytes into memory to sniff the signature.
    const headBuf = new Uint8Array(await blob.slice(0, 33).arrayBuffer());
    for (let i = 0; i < 8; i++) if (headBuf[i] !== PNG_SIG[i]) return blob;
    const IHDR_END = 33;

    // Convert DPI → pixels per metre (PNG stores it in metres).
    const ppm = Math.round(dpi * 39.37007874);

    // Build the "type + data" portion so we can CRC it in one pass.
    const typeAndData = new Uint8Array(4 + 9);
    typeAndData[0] = 0x70; typeAndData[1] = 0x48;
    typeAndData[2] = 0x59; typeAndData[3] = 0x73;
    const dv = new DataView(typeAndData.buffer, typeAndData.byteOffset, typeAndData.byteLength);
    dv.setUint32(4, ppm, false);   // X ppm (big-endian)
    dv.setUint32(8, ppm, false);   // Y ppm
    typeAndData[12] = 1;            // unit = metre
    const crc = pngCrc32(typeAndData, 0, typeAndData.length);

    // Full chunk = length(4) + type(4) + data(9) + crc(4) = 21 bytes
    const chunk = new Uint8Array(21);
    const cdv = new DataView(chunk.buffer);
    cdv.setUint32(0, 9, false); // data length
    chunk.set(typeAndData, 4);
    cdv.setUint32(17, crc, false);

    // Blob concat — the browser streams the original blob's bytes
    // without copying them into a new arraybuffer, so peak memory
    // usage stays at ~O(chunk size) = 21 bytes instead of O(blob size).
    return new Blob(
      [
        blob.slice(0, IHDR_END),                // original PNG signature + IHDR chunk
        chunk,                                   // our new pHYs chunk
        blob.slice(IHDR_END),                    // everything else (IDATs, IEND)
      ],
      { type: "image/png" }
    );
  }

  // ============= Print-ready composite generation =============
  /**
   * Render the packed layout as one or more print-ready 300 DPI PNG tiles.
   *
   * The browser canvas has a hard cap around 16,384 px per side and ~120 MP
   * total (empirically). At 300 DPI, that puts a ceiling of about 148 cm on
   * a single-canvas render. For longer sheets, we split the layout into
   * ~100 cm tiles vertically, each rendered at native 300 DPI. Staff loads
   * the tiles into the RIP in order — most professional flatbed RIPs
   * (Onyx, Caldera, Wasatch, EFI Fiery) support this natively.
   *
   * Short sheets (≤ 148 cm) still return a single-tile result — no
   * regression from the previous single-blob output.
   *
   * Each tile carries its own pHYs chunk so the RIP interprets it at the
   * correct physical dimensions rather than defaulting to 72 DPI.
   *
   * @returns {Promise<{
   *   tiles: Array<{ blob: Blob, dpi: number, widthPx: number, heightPx: number,
   *                  yStartCm: number, yEndCm: number, tileLengthCm: number,
   *                  tileIndex: number, tileTotal: number }>,
   *   dpi: number,
   *   totalWidthCm: number,
   *   totalLengthCm: number,
   *   downscaled: boolean,
   * } | null>}
   */
  async function buildCompositePng() {
    const okDesigns = designs.filter(function (d) { return d.status === "ok"; });
    if (!okDesigns.length || !layout.placed.length) return null;

    // Canvas caps. 120 MP total, 16,384 per side — well under theoretical
    // browser limits so we don't accidentally taint on Safari or older
    // Chromium builds.
    const MAX_AREA_PX = 120 * 1000 * 1000;
    const MAX_DIM_PX = 16384;
    const MM_PER_INCH = 25.4;
    const sheetLengthCm = layout.totalLengthMm / MM_PER_CM;
    const targetDpi = CFG.designDpi || 300;

    // At 300 DPI the sheet width in px is fixed:
    //   58 cm / 2.54 = 22.83 in × 300 = 6850 px
    // Wide enough that the canvas MAX_DIM (16384) doesn't limit width for
    // typical DTF sheets (< 55 cm wide at 300 DPI still fits). Length is the
    // problem — one canvas can't hold a 902 cm sheet at 300 DPI.
    const widthPx = Math.round((CFG.sheetWidthCm / MM_PER_INCH * 10) * targetDpi);
    if (widthPx > MAX_DIM_PX) {
      // Truly enormous sheet width — should never happen with our defaults.
      return null;
    }

    // Work out how long a single tile can be at target DPI without
    // exceeding either the area cap or the per-side dimension cap. The
    // tighter of the two wins.
    const maxTileHeightByArea = Math.floor(MAX_AREA_PX / widthPx);
    const maxTileHeightPx = Math.min(maxTileHeightByArea, MAX_DIM_PX);
    const maxTileLengthCm = (maxTileHeightPx / targetDpi) * MM_PER_INCH / 10;

    // We aim for round 100 cm tiles, but if that doesn't fit we take
    // whatever the canvas will bear. Below 40 cm/tile we bail out — the
    // pipeline is degenerate and staff should just use individual files.
    const desiredTileLengthCm = 100;
    const tileLengthCm = Math.min(desiredTileLengthCm, maxTileLengthCm);
    if (tileLengthCm < 40) return null;

    // How many tiles do we need?
    const totalTiles = Math.ceil(sheetLengthCm / tileLengthCm);
    if (totalTiles < 1) return null;

    // Preload every design's cropped bitmap once, shared across all tiles.
    const imgCache = {};
    for (let i = 0; i < okDesigns.length; i++) {
      const d = okDesigns[i];
      if (imgCache[d.id]) continue;
      if (!d.previewDataUrl) continue;
      try {
        const img = await new Promise(function (res, rej) {
          const im = new Image();
          im.onload = function () { res(im); };
          im.onerror = function () { rej(new Error("Couldn't load design for composite: " + d.file.name)); };
          im.src = d.previewDataUrl;
        });
        imgCache[d.id] = img;
      } catch (e) { /* skip — best effort */ }
    }
    const byId = {};
    okDesigns.forEach(function (d) { byId[d.id] = d; });

    // Render each tile in sequence. Sequential (not parallel) so peak memory
    // stays flat at one canvas + one blob + one arraybuffer worth of RGBA
    // pixels, not N×.
    const tiles = [];
    for (let ti = 0; ti < totalTiles; ti++) {
      const yStartCm = ti * tileLengthCm;
      const yEndCm = Math.min((ti + 1) * tileLengthCm, sheetLengthCm);
      const thisTileLengthCm = yEndCm - yStartCm;
      const tileHeightPx = Math.round((thisTileLengthCm / MM_PER_INCH * 10) * targetDpi);

      // Build tile canvas
      const canvas = document.createElement("canvas");
      canvas.width = widthPx;
      canvas.height = tileHeightPx;
      const ctx = canvas.getContext("2d");
      if (!ctx) { try { canvas.width = 0; canvas.height = 0; } catch (_) {} return null; }
      // Transparent background — DTF prints only opaque pixels, so leaving
      // the canvas untouched is what we want. Any fill here would ruin it.

      const yOffsetMm = yStartCm * MM_PER_CM;
      function mmToPx(mm) { return (mm / MM_PER_INCH) * targetDpi; }

      // Draw only the designs that intersect this tile. Off-canvas parts
      // clip automatically, so a design spanning two tiles renders its top
      // half in tile N and bottom half in tile N+1 without any manual math.
      layout.placed.forEach(function (p) {
        const d = byId[p.sourceId];
        if (!d) return;
        const img = imgCache[d.id];
        if (!img) return;
        // Skip designs that don't touch this tile at all.
        if ((p.yMm + p.heightMm) < yOffsetMm) return;
        if (p.yMm > (yEndCm * MM_PER_CM)) return;

        const x = mmToPx(p.xMm);
        const y = mmToPx(p.yMm - yOffsetMm);   // local y within tile
        const w = mmToPx(p.widthMm);
        const h = mmToPx(p.heightMm);
        try {
          if (p.rotated) {
            ctx.save();
            ctx.translate(x + w, y);
            ctx.rotate(Math.PI / 2);
            ctx.drawImage(img, 0, 0, h, w);
            ctx.restore();
          } else {
            ctx.drawImage(img, x, y, w, h);
          }
        } catch (_) { /* swallow */ }
      });

      // Encode this tile
      const rawBlob = await new Promise(function (res) {
        canvas.toBlob(function (b) { res(b); }, "image/png");
      });

      // Kill the canvas bitmap ASAP — freeing ~250 MB before we start on
      // the next tile prevents OOM crashes on multi-tile orders.
      try { canvas.width = 0; canvas.height = 0; } catch (_) {}

      if (!rawBlob) {
        // Give up on this tile but keep going — partial composite still
        // useful for staff to see the rest of the sheet.
        continue;
      }

      // Give the browser a chance to GC between tiles.
      await new Promise(function (r) { setTimeout(r, 0); });

      // pHYs chunk so the RIP sees native 300 DPI. Memory-efficient blob
      // slice + concat so we don't double the peak.
      let finalBlob = rawBlob;
      try { finalBlob = await injectPhysChunk(rawBlob, targetDpi); }
      catch (_) { finalBlob = rawBlob; }

      tiles.push({
        blob: finalBlob,
        dpi: targetDpi,
        widthPx: widthPx,
        heightPx: tileHeightPx,
        yStartCm: yStartCm,
        yEndCm: yEndCm,
        tileLengthCm: thisTileLengthCm,
        tileIndex: ti + 1,       // 1-indexed for cart display
        tileTotal: totalTiles,
      });
    }

    // Free the shared bitmap cache once all tiles are done.
    for (const k in imgCache) { imgCache[k] = null; }

    if (!tiles.length) return null;

    return {
      tiles: tiles,
      dpi: targetDpi,
      totalWidthCm: CFG.sheetWidthCm,
      totalLengthCm: sheetLengthCm,
      // "downscaled" retained for backwards compat with the approval modal
      // — we always render at target DPI now, never lower, so it's always false.
      downscaled: false,
    };
  }

  /**
   * Render the composite PNG onto the modal preview canvas with cm rulers
   * on top and left, on a subtle background that lets the transparency show
   * through. Same visual language as the main builder preview so the customer
   * feels continuity between designing and approving.
   *
   * Returns a Promise that resolves once the image is decoded and drawn.
   */
  function renderApprovalPreview(compositeInfo, sheetLengthCm) {
    return new Promise(function (resolve) {
      const canvas = el.approvalPreviewCanvas;
      if (!canvas || !compositeInfo || !compositeInfo.blob) { resolve(false); return; }
      const url = URL.createObjectURL(compositeInfo.blob);
      const img = new Image();
      img.onload = function () {
        try {
          const ctx = canvas.getContext("2d");
          // Layout constants (CSS pixels)
          const RULER_TOP = 26;
          const RULER_LEFT = 34;
          // Target canvas width — sized for the modal frame. Cap so it looks
          // big and readable but doesn't push the modal past viewport.
          const MAX_CSS_WIDTH = 1000;
          const sheetAspect = img.naturalWidth / img.naturalHeight;
          const sheetVisualW = Math.min(MAX_CSS_WIDTH, img.naturalWidth);
          const sheetVisualH = sheetVisualW / sheetAspect;

          const canvasW = sheetVisualW + RULER_LEFT;
          const canvasH = sheetVisualH + RULER_TOP;

          const DPR = Math.min(3, window.devicePixelRatio || 1);
          canvas.width = canvasW * DPR;
          canvas.height = canvasH * DPR;
          canvas.style.width = canvasW + "px";
          canvas.style.height = canvasH + "px";
          ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

          ctx.clearRect(0, 0, canvasW, canvasH);

          // Ruler backgrounds — subtle so they don't fight the sheet
          ctx.fillStyle = "rgba(255,255,255,0.05)";
          ctx.fillRect(0, 0, canvasW, RULER_TOP);
          ctx.fillRect(0, 0, RULER_LEFT, canvasH);

          // Sheet body: draw customer's preview bg colour so transparent
          // regions of the composite show through as the "shirt colour".
          ctx.fillStyle = previewBg;
          ctx.fillRect(RULER_LEFT, RULER_TOP, sheetVisualW, sheetVisualH);

          // Draw the composite image over the sheet bg (transparent pixels
          // pass through, ink pixels overlay).
          ctx.drawImage(img, RULER_LEFT, RULER_TOP, sheetVisualW, sheetVisualH);

          // Sheet border
          const isLightBg = isLightColor(previewBg);
          ctx.strokeStyle = isLightBg ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.32)";
          ctx.lineWidth = 1;
          ctx.strokeRect(RULER_LEFT + 0.5, RULER_TOP + 0.5, sheetVisualW - 1, sheetVisualH - 1);

          // Ruler colours
          const rulerText = "rgba(255,255,255,0.75)";
          const rulerLine = "rgba(255,255,255,0.4)";
          const rulerFaint = "rgba(255,255,255,0.18)";

          // TOP ruler — 0 → sheetWidthCm going right
          const sheetWidthCm = CFG.sheetWidthCm;
          const pxPerCmH = sheetVisualW / sheetWidthCm;
          let widthEvery = sheetWidthCm > 80 ? 10 : 5;
          ctx.font = "600 11px system-ui, -apple-system, sans-serif";
          ctx.fillStyle = rulerText;
          ctx.textBaseline = "middle";
          ctx.textAlign = "center";
          for (let cm = 0; cm <= Math.ceil(sheetWidthCm); cm++) {
            const px = RULER_LEFT + cm * pxPerCmH;
            if (px > RULER_LEFT + sheetVisualW + 2) break;
            const isMajor = cm % widthEvery === 0;
            ctx.strokeStyle = isMajor ? rulerLine : rulerFaint;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(px + 0.5, RULER_TOP - (isMajor ? 10 : 5));
            ctx.lineTo(px + 0.5, RULER_TOP);
            ctx.stroke();
            if (isMajor && cm > 0) {
              ctx.fillStyle = rulerText;
              ctx.fillText(cm + "", px, RULER_TOP - 16);
            }
          }

          // LEFT ruler — 0 → sheetLengthCm going down
          const pxPerCmV = sheetVisualH / sheetLengthCm;
          let lengthEvery = 5;
          if (sheetLengthCm > 150) lengthEvery = 25;
          else if (sheetLengthCm > 80) lengthEvery = 10;
          ctx.textAlign = "right";
          for (let cm = 0; cm <= Math.ceil(sheetLengthCm); cm++) {
            const py = RULER_TOP + cm * pxPerCmV;
            if (py > RULER_TOP + sheetVisualH + 2) break;
            const isMajor = cm % lengthEvery === 0;
            ctx.strokeStyle = isMajor ? rulerLine : rulerFaint;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(RULER_LEFT - (isMajor ? 10 : 5), py + 0.5);
            ctx.lineTo(RULER_LEFT, py + 0.5);
            ctx.stroke();
            if (isMajor && cm > 0) {
              ctx.fillStyle = rulerText;
              ctx.fillText(cm + "", RULER_LEFT - 12, py);
            }
          }

          // "cm" label in the corner
          ctx.textAlign = "center";
          ctx.font = "700 10px system-ui, -apple-system, sans-serif";
          ctx.fillStyle = rulerText;
          ctx.fillText("cm", RULER_LEFT / 2, RULER_TOP / 2);

          canvas.hidden = false;
          resolve(true);
        } catch (e) {
          resolve(false);
        } finally {
          try { URL.revokeObjectURL(url); } catch (_) {}
        }
      };
      img.onerror = function () {
        try { URL.revokeObjectURL(url); } catch (_) {}
        resolve(false);
      };
      img.src = url;
    });
  }

  /**
   * Fallback modal preview — renders the packed layout directly from
   * `layout.placed` when the composite PNG couldn't be built (e.g. for
   * very long sheets that exceed browser canvas limits at all DPI rungs).
   * Loads each design's preview image and draws them into their placed
   * positions on the modal canvas, with rulers around the outside.
   */
  async function renderApprovalPreviewFromLayout(sheetLengthCm, validDesigns) {
    const canvas = el.approvalPreviewCanvas;
    if (!canvas || !layout || !layout.placed || layout.placed.length === 0) return false;

    const RULER_TOP = 26;
    const RULER_LEFT = 34;
    // Aim for the sheet to fit within ~1000 CSS px wide on screen — for
    // very long sheets we'll get a tall canvas, which the modal handles
    // via max-height + scroll.
    const MAX_CSS_WIDTH = 1000;
    const MAX_CSS_HEIGHT = 800; // constrain very long sheets so canvas fits in modal
    const sheetWidthCm = CFG.sheetWidthCm;

    // Compute a scale that fits both dimensions
    const pxPerCmByWidth = MAX_CSS_WIDTH / sheetWidthCm;
    const pxPerCmByHeight = MAX_CSS_HEIGHT / Math.max(1, sheetLengthCm);
    const pxPerCm = Math.min(pxPerCmByWidth, pxPerCmByHeight);

    const sheetVisualW = sheetWidthCm * pxPerCm;
    const sheetVisualH = sheetLengthCm * pxPerCm;
    const canvasW = sheetVisualW + RULER_LEFT;
    const canvasH = sheetVisualH + RULER_TOP;

    const ctx = canvas.getContext("2d");
    const DPR = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = canvasW * DPR;
    canvas.height = canvasH * DPR;
    canvas.style.width = canvasW + "px";
    canvas.style.height = canvasH + "px";
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, canvasW, canvasH);

    // Ruler + sheet backgrounds
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.fillRect(0, 0, canvasW, RULER_TOP);
    ctx.fillRect(0, 0, RULER_LEFT, canvasH);
    ctx.fillStyle = previewBg;
    ctx.fillRect(RULER_LEFT, RULER_TOP, sheetVisualW, sheetVisualH);
    const isLightBg = isLightColor(previewBg);
    ctx.strokeStyle = isLightBg ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.32)";
    ctx.lineWidth = 1;
    ctx.strokeRect(RULER_LEFT + 0.5, RULER_TOP + 0.5, sheetVisualW - 1, sheetVisualH - 1);

    // Rulers
    const rulerText = "rgba(255,255,255,0.75)";
    const rulerLine = "rgba(255,255,255,0.4)";
    const rulerFaint = "rgba(255,255,255,0.18)";
    ctx.font = "600 11px system-ui, -apple-system, sans-serif";
    ctx.textBaseline = "middle";

    // Top ruler — sheet width
    const widthEvery = sheetWidthCm > 80 ? 10 : 5;
    ctx.textAlign = "center";
    for (let cm = 0; cm <= Math.ceil(sheetWidthCm); cm++) {
      const px = RULER_LEFT + cm * pxPerCm;
      if (px > RULER_LEFT + sheetVisualW + 2) break;
      const isMajor = cm % widthEvery === 0;
      ctx.strokeStyle = isMajor ? rulerLine : rulerFaint;
      ctx.beginPath();
      ctx.moveTo(px + 0.5, RULER_TOP - (isMajor ? 10 : 5));
      ctx.lineTo(px + 0.5, RULER_TOP);
      ctx.stroke();
      if (isMajor && cm > 0) {
        ctx.fillStyle = rulerText;
        ctx.fillText(cm + "", px, RULER_TOP - 16);
      }
    }
    // Left ruler — sheet length. Scale interval up for long sheets so
    // labels don't crowd (25cm every 200cm, 100cm every 500cm).
    let lengthEvery = 5;
    if (sheetLengthCm > 500) lengthEvery = 100;
    else if (sheetLengthCm > 200) lengthEvery = 50;
    else if (sheetLengthCm > 100) lengthEvery = 25;
    else if (sheetLengthCm > 50) lengthEvery = 10;
    ctx.textAlign = "right";
    for (let cm = 0; cm <= Math.ceil(sheetLengthCm); cm++) {
      const py = RULER_TOP + cm * pxPerCm;
      if (py > RULER_TOP + sheetVisualH + 2) break;
      const isMajor = cm % lengthEvery === 0;
      ctx.strokeStyle = isMajor ? rulerLine : rulerFaint;
      ctx.beginPath();
      ctx.moveTo(RULER_LEFT - (isMajor ? 10 : 5), py + 0.5);
      ctx.lineTo(RULER_LEFT, py + 0.5);
      ctx.stroke();
      if (isMajor && cm > 0) {
        ctx.fillStyle = rulerText;
        ctx.fillText(cm + "", RULER_LEFT - 12, py);
      }
    }
    ctx.textAlign = "center";
    ctx.font = "700 10px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = rulerText;
    ctx.fillText("cm", RULER_LEFT / 2, RULER_TOP / 2);

    // Load designs' preview images (once each), then draw placed rects
    const byId = {};
    const imgCache = {};
    validDesigns.forEach(function (d) { byId[d.id] = d; });
    const loadPromises = [];
    validDesigns.forEach(function (d) {
      if (!d.previewDataUrl) return;
      loadPromises.push(new Promise(function (res) {
        const im = new Image();
        im.onload = function () { imgCache[d.id] = im; res(); };
        im.onerror = function () { res(); };
        im.src = d.previewDataUrl;
      }));
    });
    await Promise.all(loadPromises);

    layout.placed.forEach(function (p) {
      const d = byId[p.sourceId];
      if (!d) return;
      const img = imgCache[d.id];
      // Preview scale: mm → cm → CSS px
      const x = RULER_LEFT + (p.xMm / 10) * pxPerCm;
      const y = RULER_TOP + (p.yMm / 10) * pxPerCm;
      const w = (p.widthMm / 10) * pxPerCm;
      const h = (p.heightMm / 10) * pxPerCm;
      if (img) {
        try {
          if (p.rotated) {
            ctx.save();
            ctx.translate(x + w, y);
            ctx.rotate(Math.PI / 2);
            ctx.drawImage(img, 0, 0, h, w);
            ctx.restore();
          } else {
            ctx.drawImage(img, x, y, w, h);
          }
        } catch (_) { /* fall through to placeholder */ }
      } else {
        ctx.fillStyle = "rgba(255,107,0,0.25)";
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = "#ff6b00";
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, w, h);
      }
    });
    canvas.hidden = false;
    return true;
  }

  // ============= Approval modal =============
  /**
   * Show the pre-checkout approval modal with the composite PNG at scale +
   * order summary + explicit approval checkbox. Returns a Promise that
   * resolves to `true` if the customer approves, `false` if they cancel.
   *
   * The modal is populated as inputs become available — first the summary
   * (immediate), then the composite preview (once buildCompositePng resolves).
   *
   * @param {{blob: Blob, dpi: number, widthPx: number, heightPx: number, downscaled: boolean} | null} compositeInfo
   * @param {{price: number, tier: number, rate: number, hitMin: boolean}} priced
   * @param {number} lengthCm
   * @param {Array<Object>} okDesigns
   * @returns {Promise<boolean>}
   */
  function showApprovalModal(compositeInfo, priced, lengthCm, okDesigns) {
    return new Promise(function (resolve) {
      const modal = el.approvalModal;
      if (!modal) { resolve(true); return; } // no modal in DOM — skip approval

      const totalCopies = okDesigns.reduce(function (s, d) {
        return s + Math.max(1, d.quantity || 1);
      }, 0);

      // Fill the summary block
      if (el.approvalSize) {
        el.approvalSize.textContent = CFG.sheetWidthCm + " × " + lengthCm.toFixed(1) + " cm";
      }
      // Prominent dims chip in the preview header
      if (el.approvalDims) {
        el.approvalDims.textContent = CFG.sheetWidthCm + " × " + lengthCm.toFixed(1) + " cm";
      }
      if (el.approvalUnique) el.approvalUnique.textContent = String(okDesigns.length);
      if (el.approvalTotal) el.approvalTotal.textContent = String(totalCopies);
      if (el.approvalRate) {
        el.approvalRate.textContent = formatDisplayRate(priced.rate, priced.tier);
      }
      if (el.approvalPrice) el.approvalPrice.textContent = formatDisplayPrice(priced.price);

      // Collect any active warnings across all designs so the customer sees
      // them one more time before approving.
      const allWarnings = [];
      okDesigns.forEach(function (d) {
        const ws = buildDesignWarnings(d);
        ws.forEach(function (w) {
          if (w.level === "error" || w.level === "warn") {
            allWarnings.push({ file: d.file.name, text: w.text });
          }
        });
      });
      if (el.approvalWarnings && el.approvalWarningsList) {
        el.approvalWarningsList.innerHTML = "";
        if (allWarnings.length) {
          allWarnings.forEach(function (w) {
            const li = document.createElement("li");
            li.textContent = w.file + ": " + w.text;
            el.approvalWarningsList.appendChild(li);
          });
          el.approvalWarnings.hidden = false;
        } else {
          el.approvalWarnings.hidden = true;
        }
      }

      // Composite preview — render onto a canvas with rulers around it so
      // the customer sees the print-ready file with proper scale context.
      if (el.approvalPreview) {
        el.approvalPreview.hidden = true;
        el.approvalPreview.removeAttribute("src");
      }
      if (el.approvalPreviewCanvas) el.approvalPreviewCanvas.hidden = true;
      if (el.approvalPreviewLoading) el.approvalPreviewLoading.hidden = false;
      if (el.approvalPreviewNote) el.approvalPreviewNote.textContent = "";

      // Composite is now always tile-based. Single-tile: use that tile's
      // blob as the preview (bit-perfect print-ready view). Multi-tile:
      // showing only tile 1 would confuse the customer, so we fall back
      // to the schematic layout preview which shows the whole sheet at once.
      const tiles = (compositeInfo && compositeInfo.tiles) || [];
      const singleTilePreview = tiles.length === 1 ? tiles[0] : null;

      if (singleTilePreview) {
        if (el.approvalPreviewNote) {
          el.approvalPreviewNote.textContent =
            singleTilePreview.widthPx + " × " + singleTilePreview.heightPx +
            " px · " + singleTilePreview.dpi + " DPI print-ready file";
        }
        // Adapt to the old renderApprovalPreview signature: it reads
        // compositeInfo.blob, so pass a synthetic object with the tile's blob.
        const previewCompositeInfo = {
          blob: singleTilePreview.blob,
          dpi: singleTilePreview.dpi,
          widthPx: singleTilePreview.widthPx,
          heightPx: singleTilePreview.heightPx,
        };
        renderApprovalPreview(previewCompositeInfo, lengthCm).then(function (ok) {
          if (el.approvalPreviewLoading) el.approvalPreviewLoading.hidden = true;
          if (!ok && el.approvalPreview) {
            const url = URL.createObjectURL(singleTilePreview.blob);
            el.approvalPreview.style.display = "";
            el.approvalPreview.onload = function () { el.approvalPreview.hidden = false; };
            el.approvalPreview.src = url;
          }
        });
      } else if (tiles.length > 1) {
        if (el.approvalPreviewNote) {
          el.approvalPreviewNote.textContent =
            "Long sheet — will be delivered as " + tiles.length + " × " +
            Math.round(tiles[0].tileLengthCm) + " cm tiles at " + tiles[0].dpi +
            " DPI (staff arranges them end-to-end in the RIP). " +
            "Preview below shows the full sheet layout.";
        }
        renderApprovalPreviewFromLayout(lengthCm, okDesigns).then(function (ok) {
          if (el.approvalPreviewLoading) el.approvalPreviewLoading.hidden = true;
          if (!ok && el.approvalPreviewNote) {
            el.approvalPreviewNote.textContent =
              "Preview couldn't be built — staff will use individual design files";
          }
        });
      } else {
        // Composite failed — extreme sheet length. Fall back to rendering
        // the layout directly from the packed rectangles + design image
        // caches so the customer still sees exactly what they're approving.
        if (el.approvalPreviewNote) {
          el.approvalPreviewNote.textContent =
            "Extra-long sheet — showing schematic preview. Individual design files ship at 300 DPI native.";
        }
        renderApprovalPreviewFromLayout(lengthCm, okDesigns).then(function (ok) {
          if (el.approvalPreviewLoading) el.approvalPreviewLoading.hidden = true;
          if (!ok && el.approvalPreviewNote) {
            el.approvalPreviewNote.textContent =
              "Preview couldn't be built — staff will use individual design files";
          }
        });
      }

      // Reset the checkbox + button state
      if (el.approvalCheckbox) el.approvalCheckbox.checked = false;
      if (el.approvalContinue) el.approvalContinue.disabled = true;

      // Reset the status pill in the sticky footer
      function updateActionsStatus() {
        if (!el.approvalActionsStatus) return;
        const ready = el.approvalCheckbox && el.approvalCheckbox.checked;
        el.approvalActionsStatus.classList.toggle("is-ready", ready);
        const iconEl = el.approvalActionsStatus.querySelector(".gsb__approval-modal-actions-status-icon");
        const textEl = el.approvalActionsStatus.querySelector("span:last-child");
        if (iconEl) iconEl.textContent = ready ? "✓" : "◯";
        if (textEl) textEl.textContent = ready
          ? "Ready — click Approve & add to cart to continue"
          : "Tick the approval box above to continue";
      }
      updateActionsStatus();

      // Scroll hint — show if the scroll container has more content below the
      // current viewport. Hide when the customer reaches the bottom.
      function updateScrollHint() {
        if (!el.approvalScroll || !el.approvalScrollHint) return;
        const canScroll = el.approvalScroll.scrollHeight - el.approvalScroll.scrollTop
          - el.approvalScroll.clientHeight > 12;
        el.approvalScrollHint.classList.toggle("is-hidden", !canScroll);
      }
      function onScroll() { updateScrollHint(); }
      // Reset scroll to top when opening + measure after layout
      if (el.approvalScroll) el.approvalScroll.scrollTop = 0;
      setTimeout(updateScrollHint, 0);
      // Also re-check after the composite preview renders (canvas dims change layout)
      setTimeout(updateScrollHint, 500);
      setTimeout(updateScrollHint, 1500);
      if (el.approvalScroll) el.approvalScroll.addEventListener("scroll", onScroll, { passive: true });

      // Wire handlers — remove them on close so we don't stack.
      function onCheckbox() {
        if (el.approvalContinue) el.approvalContinue.disabled = !el.approvalCheckbox.checked;
        updateActionsStatus();
      }
      function cleanup() {
        // cleanup() runs from a click on a button INSIDE this modal
        // (Approve or Cancel) — that button still holds focus at this
        // point. Setting aria-hidden="true" on an ancestor while a
        // descendant retains focus is invalid per the ARIA spec and
        // browsers warn/block it (focus would be hidden from assistive
        // tech). Blur first so focus returns to the document body before
        // the modal disappears.
        if (document.activeElement && modal.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        modal.hidden = true;
        modal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        if (el.approvalCheckbox) el.approvalCheckbox.removeEventListener("change", onCheckbox);
        if (el.approvalContinue) el.approvalContinue.removeEventListener("click", onContinue);
        el.approvalCancels.forEach(function (btn) { btn.removeEventListener("click", onCancel); });
        if (el.approvalScroll) el.approvalScroll.removeEventListener("scroll", onScroll);
        document.removeEventListener("keydown", onKeydown);
      }
      function onContinue() { cleanup(); resolve(true); }
      function onCancel() { cleanup(); resolve(false); }
      function onKeydown(e) {
        if (e.key === "Escape") onCancel();
      }

      if (el.approvalCheckbox) el.approvalCheckbox.addEventListener("change", onCheckbox);
      if (el.approvalContinue) el.approvalContinue.addEventListener("click", onContinue);
      el.approvalCancels.forEach(function (btn) { btn.addEventListener("click", onCancel); });
      document.addEventListener("keydown", onKeydown);

      // Show the modal + lock body scroll
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
    });
  }

  // ============= Server-side composite rendering =============
  // POSTs the packed layout to the gang-sheet-compositor service (Fly.io).
  // Server fetches every design URL, composites into ONE clean 300 DPI PNG
  // at native resolution, embeds pHYs metadata, uploads to R2, returns URL.
  //
  // Why server-side: browser canvas is memory-capped at ~120–200 MP, which
  // limits gang sheets to about 148 cm at 300 DPI. Server-side (libvips-
  // backed sharp) has no such limit — a 900 cm × 58 cm sheet at 300 DPI is
  // ~730 MP and comfortably fits on a 1 GB Fly.io instance.
  //
  // Returns { url, key, widthPx, heightPx, dpi, sizeBytes, tookMs } on
  // success, or throws on failure. Caller is responsible for surfacing the
  // failure to the customer.
  async function renderServerSideComposite(okDesigns) {
    if (!CFG.compositorUrl) throw new Error("Server compositor URL not configured");

    const lengthCm = layout.totalLengthMm / MM_PER_CM;
    const byId = {};
    okDesigns.forEach(function (d) { byId[d.id] = d; });

    // Build the placement list from the packed layout. Each placement points
    // at the design's uploaded R2 URL (server fetches from there).
    const placements = [];
    layout.placed.forEach(function (p) {
      const d = byId[p.sourceId];
      if (!d || !d.uploadedUrl) return;
      placements.push({
        designUrl: d.uploadedUrl,
        xCm: +(p.xMm / MM_PER_CM).toFixed(3),
        yCm: +(p.yMm / MM_PER_CM).toFixed(3),
        widthCm: +(p.widthMm / MM_PER_CM).toFixed(3),
        heightCm: +(p.heightMm / MM_PER_CM).toFixed(3),
        rotated: !!p.rotated,
      });
    });

    if (!placements.length) throw new Error("No placements to render");

    const body = {
      sheetWidthCm: CFG.sheetWidthCm,
      sheetLengthCm: +lengthCm.toFixed(2),
      dpi: CFG.designDpi || 300,
      placements: placements,
    };

    // The print-ready composite is a hard requirement — staff must never
    // have to hand-assemble a normal order. That no longer means checkout
    // itself has to survive the full 4-5+ minute render: the
    // orders/paid webhook (ai-design-worker's /api/gang-sheet/order-webhook)
    // now regenerates the composite server-to-server, with no time limit
    // and no customer browser tab involved, for any order whose
    // "Print-ready sheet" property isn't already a real URL by the time the
    // order is placed. So this can safely give up fast: small/short sheets
    // that render quickly still get the nice instant result here, and
    // anything slower falls through to the individual-files + layout-map
    // properties addToCart() already attaches — which is exactly the input
    // the webhook needs to finish the job automatically afterward. Checkout
    // speed and guaranteed auto-compositing are no longer in tension.
    const COMPOSITE_TIMEOUT_MS = 20000;
    const controller = new AbortController();
    const timeoutId = setTimeout(function () { controller.abort(); }, COMPOSITE_TIMEOUT_MS);
    try {
      const r = await fetch(CFG.compositorUrl + "/composite-gang-sheet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!r.ok) {
        let msg = "Composite failed (" + r.status + ")";
        try { const j = await r.json(); if (j && j.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }
      const data = await r.json();
      if (!data || !data.url) throw new Error("Composite returned no URL");
      return data;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // ============= Cart integration =============
  async function addToCart() {
    if (el.addToCartBtn.disabled) return;
    if (!CFG.variantId) {
      showCartError("Section isn't wired to a Shopify product. Set the variant ID in the theme editor.");
      return;
    }
    const okDesigns = designs.filter(function (d) { return d.status === "ok" && d.uploadedUrl; });
    if (!okDesigns.length) {
      showCartError("No designs are ready to add yet.");
      return;
    }
    const lengthCm = layout.totalLengthMm / MM_PER_CM;
    const priced = computePrice(layout.totalLengthMm);
    const price = priced.price;

    // Build line item properties. Order matters — Shopify preserves insertion
    // order in the admin, so the print-ready composite lands at the top where
    // staff can grab it immediately.
    const totalCopiesAll = okDesigns.reduce(function (s, d) {
      return s + Math.max(1, d.quantity || 1);
    }, 0);
    const props = {};

    el.addToCartBtn.disabled = true;
    const btnLabel = el.addToCartBtn.querySelector("span");
    showCartError("");

    // Step 1: Show approval modal with the schematic layout preview.
    // The customer sees the exact same packed layout they've been designing
    // with — no need to render a full-resolution composite upfront just
    // for approval. The real 300 DPI print file gets generated server-side
    // AFTER approval, so we don't waste compute on cancelled carts.
    btnLabel.textContent = "Getting your approval…";
    // Passing null compositeInfo triggers the modal's schematic-preview path
    // (renderApprovalPreviewFromLayout), which draws the packed layout
    // directly from layout.placed + design bitmaps.
    const approved = await showApprovalModal(null, priced, lengthCm, okDesigns);
    if (!approved) {
      el.addToCartBtn.disabled = false;
      btnLabel.textContent = "Add gang sheet to cart";
      return;
    }

    // Step 2: Approved. Give the server compositor a short window to render
    // the print-ready file inline — instant for small/short sheets. Bounded
    // (see renderServerSideComposite) so checkout is never held hostage to
    // it; anything slower falls through to addToCart()'s individual-files +
    // layout-map properties, which the orders/paid webhook then uses to
    // finish the composite automatically after checkout, no browser
    // involved. The elapsed-time counter is honest about the short wait
    // rather than faking a percentage.
    btnLabel.textContent = "Checking for a print-ready file…";
    const compositeStarted = Date.now();
    let compositeProgressInterval = null;
    if (el.compositeProgress && el.compositeProgressText) {
      el.compositeProgress.hidden = false;
      const tick = function () {
        const secs = Math.round((Date.now() - compositeStarted) / 1000);
        el.compositeProgressText.textContent = "Building your print file… " + secs + "s";
      };
      tick();
      compositeProgressInterval = setInterval(tick, 1000);
    }

    let compositor = null;
    let compositorError = null;
    if (CFG.compositorUrl) {
      try {
        compositor = await renderServerSideComposite(okDesigns);
      } catch (e) {
        compositor = null;
        compositorError = e.message || "compositor failed";
      }
    } else {
      compositorError = "Server compositor URL not configured on the section — set data-compositor-url or enable it in the theme editor.";
    }

    if (compositeProgressInterval) clearInterval(compositeProgressInterval);
    if (el.compositeProgress) el.compositeProgress.hidden = true;

    if (compositor && compositor.url) {
      // Derive the R2 key from the returned URL so we can build the
      // worker's download + transparency-preview links.
      const KEY_MATCH = /(customer-uploads|designs|vectorised)\/.+$/;
      const m = compositor.url.match(KEY_MATCH);
      const compositeKey = m ? m[0] : "";

      props["Print-ready sheet"] = compositor.url;
      if (compositeKey && CFG.workerUrl) {
        props["Print-ready download"] =
          CFG.workerUrl + "/api/download?key=" + encodeURIComponent(compositeKey);
        props["Print-ready preview"] =
          CFG.workerUrl + "/api/preview?key=" + encodeURIComponent(compositeKey);
      }
      props["Print-ready size"] =
        compositor.widthPx + " × " + compositor.heightPx + " px @ " + compositor.dpi + " DPI";
      props["Print-ready note"] =
        "Server-rendered composite. Single 300 DPI print file — load straight into your RIP.";
    } else {
      // Didn't finish inline within the short checkout-time window — still
      // let the customer complete the order immediately. The orders/paid
      // webhook picks this up right after and regenerates the composite
      // server-side with no time limit, then appends the result straight
      // onto this order's note — that's the normal path for any sheet too
      // big to render in a few seconds, not a failure. Individual files +
      // Layout map stay attached as the safety net in case that also
      // doesn't come through for some reason.
      props["Print-ready sheet"] =
        "(not ready yet" + (compositorError ? ": " + compositorError : "") + " — auto-composite will complete shortly after checkout; check this order's notes, or use individual 300 DPI files + Layout map below if it doesn't)";
      props["Print-ready note"] =
        "⏳ Print-ready file is being generated automatically — check this order's notes shortly. Individual design files + Layout map JSON below as a fallback if it doesn't show up.";
    }

    // Step 2: order metadata
    props["Sheet type"] = "DTF Gang Sheet";
    props["Sheet width"] = CFG.sheetWidthCm + " cm";
    props["Sheet length used"] = lengthCm.toFixed(1) + " cm";
    props["Unique designs"] = String(okDesigns.length);
    props["Total transfers"] = String(totalCopiesAll);
    props["Rate applied"] = "$" + priced.rate.toFixed(2) + "/cm (tier " + priced.tier + ")";
    if (priced.hitMin) props["Note"] = "Minimum charge applied ($" + CFG.minChargeAud + ")";
    // Display price mirrors what the customer saw in the builder — inc-GST
    // if the section has that mode on, ex-GST otherwise. The cart consolidator
    // (ai-designer.js) prefers this over Shopify's raw line_price so the cart
    // summary row matches the quote the customer approved.
    props["Display price"] = formatDisplayPrice(priced.price);
    // Also stash the raw inc-GST cents so the consolidator can format
    // consistently across themes without re-parsing the string.
    props["Display price cents"] = String(Math.round(grossPrice(priced.price).amount * 100));

    // Approval record — customer explicitly ticked the "I approve this layout"
    // box in the pre-checkout modal. Timestamp lets staff verify.
    props["Customer approved layout"] = "Yes (" + new Date().toISOString() + ")";

    // Step 3: individual design URLs — kept as a backup so staff can re-print
    // any single design at native resolution without re-cropping.
    okDesigns.forEach(function (d, i) {
      const n = i + 1;
      const qty = Math.max(1, d.quantity || 1);
      props["Design " + n + " file"] = d.uploadedUrl;
      props["Design " + n + " size"] =
        d.widthCm.toFixed(1) + " × " + d.heightCm.toFixed(1) + " cm × " + qty;
      props["Design " + n + " filename"] = d.file.name;
      const notes = [];
      if (d.trimmed) notes.push("Empty edges auto-trimmed");
      if (d.colorMode && d.colorMode.mode && d.colorMode.mode !== "rgb" && d.colorMode.mode !== "rgba") {
        notes.push("Colour mode: " + d.colorMode.mode);
      }
      if (notes.length) props["Design " + n + " note"] = notes.join(" · ");
    });

    // Step 4: layout map — a compact JSON manifest describing where each
    // placed rect sits on the sheet. Always included so staff can:
    //   a) Verify the composite matches what the customer previewed
    //   b) Rebuild the sheet in their RIP if the composite fell back or failed
    // Coordinates are top-left origin, values in centimetres.
    try {
      const layoutJson = {
        sheetWidthCm: CFG.sheetWidthCm,
        sheetLengthCm: parseFloat(lengthCm.toFixed(2)),
        gapMm: CFG.gapMm,
        placements: layout.placed.map(function (p) {
          const d = okDesigns.find(function (x) { return x.id === p.sourceId; });
          return {
            filename: d ? d.file.name : null,
            designUrl: d ? d.uploadedUrl : null,
            xCm: +(p.xMm / 10).toFixed(2),
            yCm: +(p.yMm / 10).toFixed(2),
            widthCm: +(p.widthMm / 10).toFixed(2),
            heightCm: +(p.heightMm / 10).toFixed(2),
            rotated: !!p.rotated,
          };
        }),
      };
      props["Layout map (JSON)"] = JSON.stringify(layoutJson);
    } catch (_) { /* non-fatal */ }

    // Quantity = ceil(price in dollars) so the $1 gang sheet product hits the right total.
    const quantity = Math.max(1, Math.ceil(grossPrice(price).amount)); /* GST-inclusive: bill grossed amount via $1 unit */

    btnLabel.textContent = "Adding to cart…";

    try {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          items: [{
            id: CFG.variantId,
            quantity: quantity,
            properties: props,
          }],
        }),
        credentials: "same-origin",
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || ("Cart add failed (" + r.status + ")"));
      }
      // Success — go to cart
      window.location.href = "/cart";
    } catch (e) {
      showCartError(e.message || "Couldn't add to cart — try again.");
      el.addToCartBtn.disabled = false;
      btnLabel.textContent = "Add gang sheet to cart";
    }
  }

  // ============= Event listeners =============
  if (el.fileInput) {
    el.fileInput.addEventListener("change", function (e) {
      const files = e.target.files;
      handleFileList(files);
      e.target.value = ""; // reset so re-uploading the same file works
    });
  }

  if (el.drop) {
    ["dragenter", "dragover"].forEach(function (evt) {
      el.drop.addEventListener(evt, function (e) {
        e.preventDefault();
        el.drop.classList.add("is-dragover");
      });
    });
    ["dragleave", "drop"].forEach(function (evt) {
      el.drop.addEventListener(evt, function (e) {
        e.preventDefault();
        el.drop.classList.remove("is-dragover");
      });
    });
    el.drop.addEventListener("drop", function (e) {
      if (e.dataTransfer && e.dataTransfer.files) {
        handleFileList(e.dataTransfer.files);
      }
    });
  }

  if (el.addMore) {
    el.addMore.addEventListener("click", function () {
      if (el.fileInput) el.fileInput.click();
    });
  }

  if (el.addToCartBtn) {
    el.addToCartBtn.addEventListener("click", addToCart);
  }

  // Preview background swatches — click sets the background colour for the
  // preview canvas only. Composite PNG uploaded to R2 stays transparent.
  if (el.bgSwatches && el.bgSwatches.length) {
    el.bgSwatches.forEach(function (sw) {
      sw.addEventListener("click", function () {
        const hex = sw.getAttribute("data-bg");
        if (hex) setPreviewBg(hex);
      });
    });
  }
  if (el.bgCustomInput) {
    el.bgCustomInput.addEventListener("input", function () {
      setPreviewBg(el.bgCustomInput.value);
    });
    // On some browsers "change" fires when the picker closes; on others only
    // "input" fires while dragging. Listen to both so the state is consistent.
    el.bgCustomInput.addEventListener("change", function () {
      setPreviewBg(el.bgCustomInput.value);
    });
  }
  // Apply any persisted colour on load so the swatch UI matches previewBg.
  syncSwatches();

  // ============= Save-and-come-back session persistence =============
  // Stores the customer's builder state in localStorage so they can close
  // the tab and resume later. Only lightweight metadata is stored — full
  // pixel data is refetched from the R2 URL on restore, keeping the
  // localStorage payload well under quota.
  const SESSION_STORAGE_KEY = "gsb_session_v1";
  const SESSION_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  let sessionSaveTimer = null;

  /**
   * Serialise the current session to localStorage. Debounced 400ms so bursts
   * of state changes coalesce to one write.
   */
  function scheduleSessionSave() {
    if (sessionSaveTimer) clearTimeout(sessionSaveTimer);
    sessionSaveTimer = setTimeout(saveSession, 400);
  }

  function saveSession() {
    try {
      // Guard: if there's nothing to save, don't overwrite existing data.
      // This protects returning customers from having their session wiped
      // by the initial synchronous repackAndRender() before restoreSession's
      // async fetches have populated the designs list.
      const hasDesigns = designs.some(function (d) { return d.uploadedUrl && d.status === "ok"; });
      const hasNnRows = nnState.rows.some(function (r) {
        return (r.name && r.name.trim()) || (r.number && r.number.trim());
      });
      if (!hasDesigns && !hasNnRows) return;

      const savableDesigns = designs
        .filter(function (d) { return d.uploadedUrl && d.status === "ok"; })
        .map(function (d) {
          return {
            id: d.id,
            filename: d.file && d.file.name ? d.file.name : "design.png",
            uploadedUrl: d.uploadedUrl,
            widthCm: d.widthCm,
            heightCm: d.heightCm,
            detectedWidthCm: d.detectedWidthCm,
            detectedHeightCm: d.detectedHeightCm,
            aspectRatio: d.aspectRatio,
            quantity: d.quantity,
            trimmed: d.trimmed,
            croppedWidthPx: d.croppedWidthPx,
            croppedHeightPx: d.croppedHeightPx,
            colorMode: d.colorMode,
            allowRotation: d.allowRotation !== false,
          };
        });

      const savableNnRows = nnState.rows
        .filter(function (r) { return (r.name && r.name.trim()) || (r.number && r.number.trim()); })
        .map(function (r) { return { name: r.name, number: r.number }; });

      const snapshot = {
        v: 1,
        timestamp: Date.now(),
        designs: savableDesigns,
        nn: {
          mode: nnState.mode,
          style: nnState.style,
          sportsColour: nnState.sportsColour,
          sportsSize: nnState.sportsSize,
          customFont: nnState.customFont,
          customColour: nnState.customColour,
          customHeightMm: nnState.customHeightMm,
          rows: savableNnRows,
        },
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(snapshot));
    } catch (_) { /* localStorage full or disabled — silent */ }
  }

  function clearSession() {
    try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch (_) {}
    // Also purge in-memory state so the reload page reflects it
    designs.length = 0;
    if (el.designsList) el.designsList.innerHTML = "";
    if (el.designsStep) el.designsStep.hidden = true;
    nnState.rows.length = 0;
    if (el.nnRowList) el.nnRowList.innerHTML = "";
    updateNnCount();
    if (el.restoreBanner) el.restoreBanner.hidden = true;
    repackAndRender();
  }

  function humanRelativeTime(timestamp) {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes + " minute" + (minutes === 1 ? "" : "s") + " ago";
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + " hour" + (hours === 1 ? "" : "s") + " ago";
    const days = Math.floor(hours / 24);
    if (days < 30) return days + " day" + (days === 1 ? "" : "s") + " ago";
    return "over a month ago";
  }

  /**
   * Reconstruct a design object from its stored metadata + refetched preview
   * data URL. Returns the design or null if the upload URL no longer resolves.
   */
  async function restoreDesignFromStored(stored) {
    if (!stored || !stored.uploadedUrl) return null;
    let previewDataUrl = null;
    try {
      const resp = await fetch(stored.uploadedUrl, { credentials: "omit" });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      previewDataUrl = await new Promise(function (res, rej) {
        const reader = new FileReader();
        reader.onload = function () { res(reader.result); };
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
    } catch (_) {
      return null;
    }
    const design = {
      id: stored.id || makeId(),
      file: { name: stored.filename || "design.png" },
      uploadFile: null,
      originalWidthPx: stored.croppedWidthPx || 0,
      originalHeightPx: stored.croppedHeightPx || 0,
      croppedWidthPx: stored.croppedWidthPx || 0,
      croppedHeightPx: stored.croppedHeightPx || 0,
      trimmed: !!stored.trimmed,
      widthCm: stored.widthCm || 0,
      heightCm: stored.heightCm || 0,
      detectedWidthCm: stored.detectedWidthCm || stored.widthCm || 0,
      detectedHeightCm: stored.detectedHeightCm || stored.heightCm || 0,
      aspectRatio: stored.aspectRatio || 1,
      quantity: stored.quantity || 1,
      previewDataUrl: previewDataUrl,
      colorMode: stored.colorMode || { format: "png", mode: "rgba", bitDepth: 8, hasAlpha: true },
      printStats: null,
      allowRotation: stored.allowRotation !== false,
      uploadedUrl: stored.uploadedUrl,
      status: "ok",
    };
    return design;
  }

  async function restoreSession() {
    let snapshot;
    try {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return;
      snapshot = JSON.parse(raw);
    } catch (_) {
      return;
    }
    if (!snapshot || !snapshot.timestamp) return;
    if (Date.now() - snapshot.timestamp > SESSION_EXPIRY_MS) {
      try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch (_) {}
      return;
    }

    let restoredDesigns = 0;
    let restoredNnRows = 0;

    // ---- Designs ----
    if (snapshot.designs && snapshot.designs.length) {
      for (let i = 0; i < snapshot.designs.length; i++) {
        const design = await restoreDesignFromStored(snapshot.designs[i]);
        if (!design) continue;
        designs.push(design);
        if (el.designsStep) el.designsStep.hidden = false;
        const row = renderDesignRow(design);
        if (row && el.designsList) el.designsList.appendChild(row);
        restoredDesigns++;
      }
    }

    // ---- N&N state ----
    if (snapshot.nn) {
      if (snapshot.nn.sportsColour) nnState.sportsColour = snapshot.nn.sportsColour;
      if (snapshot.nn.sportsSize) nnState.sportsSize = snapshot.nn.sportsSize;
      if (snapshot.nn.customFont) nnState.customFont = snapshot.nn.customFont;
      if (snapshot.nn.customColour) nnState.customColour = snapshot.nn.customColour;
      if (snapshot.nn.customHeightMm) nnState.customHeightMm = snapshot.nn.customHeightMm;

      if (snapshot.nn.rows && snapshot.nn.rows.length) {
        snapshot.nn.rows.forEach(function (r) {
          addNnRow(r.name || "", r.number || "");
        });
        restoredNnRows = snapshot.nn.rows.length;
      }

      // Sync UI to the restored values
      if (el.nnSportsSize) el.nnSportsSize.value = nnState.sportsSize;
      if (el.nnCustomHeight) el.nnCustomHeight.value = String(nnState.customHeightMm);
      if (el.nnColourSwatches) {
        el.nnColourSwatches.forEach(function (sw) {
          sw.classList.toggle("is-active", (sw.getAttribute("data-gsb-nn-colour") || "").toLowerCase() === nnState.sportsColour.toLowerCase());
        });
      }
      if (el.nnCustomColourSwatches) {
        el.nnCustomColourSwatches.forEach(function (sw) {
          sw.classList.toggle("is-active", (sw.getAttribute("data-gsb-nn-custom-colour") || "").toLowerCase() === nnState.customColour.toLowerCase());
        });
      }
      if (el.nnFontButtons) {
        el.nnFontButtons.forEach(function (btn) {
          btn.classList.toggle("is-active", btn.getAttribute("data-gsb-nn-font") === nnState.customFont);
        });
      }
      if (snapshot.nn.style) setNnStyle(snapshot.nn.style);
      if (snapshot.nn.mode) setMode(snapshot.nn.mode);
    }

    // Show the restore banner if anything came back
    if ((restoredDesigns > 0 || restoredNnRows > 0) && el.restoreBanner) {
      const bits = [];
      if (restoredDesigns > 0) bits.push(restoredDesigns + " design" + (restoredDesigns === 1 ? "" : "s"));
      if (restoredNnRows > 0) bits.push(restoredNnRows + " team " + (restoredNnRows === 1 ? "row" : "rows"));
      const summary = bits.join(" + ") + " from " + humanRelativeTime(snapshot.timestamp);
      if (el.restoreSummary) el.restoreSummary.textContent = summary;
      el.restoreBanner.hidden = false;
      // Auto-hide after 12s so it doesn't linger forever
      setTimeout(function () {
        if (el.restoreBanner) el.restoreBanner.hidden = true;
      }, 12000);
    }

    if (restoredDesigns > 0) repackAndRender();
  }

  // Wire the banner buttons
  if (el.restoreClear) {
    el.restoreClear.addEventListener("click", function () {
      if (confirm("Clear your restored session and start with a blank builder?")) {
        clearSession();
      }
    });
  }
  if (el.restoreDismiss) {
    el.restoreDismiss.addEventListener("click", function () {
      if (el.restoreBanner) el.restoreBanner.hidden = true;
    });
  }

  // Kick off session restore before anything else user-driven. Not awaited —
  // designs restore progressively as their URLs fetch.
  restoreSession();

  // Names & Numbers wiring
  if (el.modeToggleBtns && el.modeToggleBtns.length) {
    el.modeToggleBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        setMode(btn.getAttribute("data-gsb-mode") || "upload");
      });
    });
  }
  if (el.nnStyleTabs && el.nnStyleTabs.length) {
    el.nnStyleTabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        setNnStyle(tab.getAttribute("data-gsb-nn-style") || "sports");
      });
    });
  }
  // Sports colour swatches
  if (el.nnColourSwatches && el.nnColourSwatches.length) {
    el.nnColourSwatches.forEach(function (sw) {
      sw.addEventListener("click", function () {
        const hex = sw.getAttribute("data-gsb-nn-colour");
        if (!hex) return;
        nnState.sportsColour = hex;
        el.nnColourSwatches.forEach(function (other) { other.classList.remove("is-active"); });
        sw.classList.add("is-active");
        if (el.nnColourCustom) el.nnColourCustom.value = hex;
      });
    });
  }
  if (el.nnColourCustom) {
    el.nnColourCustom.addEventListener("input", function () {
      nnState.sportsColour = el.nnColourCustom.value;
      // Deselect preset swatches — custom is active
      if (el.nnColourSwatches) el.nnColourSwatches.forEach(function (sw) { sw.classList.remove("is-active"); });
    });
  }
  if (el.nnSportsSize) {
    el.nnSportsSize.addEventListener("change", function () {
      nnState.sportsSize = el.nnSportsSize.value || "adult";
    });
  }
  // Custom fonts
  if (el.nnFontButtons && el.nnFontButtons.length) {
    el.nnFontButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        const font = btn.getAttribute("data-gsb-nn-font");
        if (!font) return;
        nnState.customFont = font;
        el.nnFontButtons.forEach(function (other) { other.classList.remove("is-active"); });
        btn.classList.add("is-active");
      });
    });
  }
  // Custom colour swatches
  if (el.nnCustomColourSwatches && el.nnCustomColourSwatches.length) {
    el.nnCustomColourSwatches.forEach(function (sw) {
      sw.addEventListener("click", function () {
        const hex = sw.getAttribute("data-gsb-nn-custom-colour");
        if (!hex) return;
        nnState.customColour = hex;
        el.nnCustomColourSwatches.forEach(function (other) { other.classList.remove("is-active"); });
        sw.classList.add("is-active");
        if (el.nnCustomColourPicker) el.nnCustomColourPicker.value = hex;
      });
    });
  }
  if (el.nnCustomColourPicker) {
    el.nnCustomColourPicker.addEventListener("input", function () {
      nnState.customColour = el.nnCustomColourPicker.value;
      if (el.nnCustomColourSwatches) el.nnCustomColourSwatches.forEach(function (sw) { sw.classList.remove("is-active"); });
    });
  }
  if (el.nnCustomHeight) {
    el.nnCustomHeight.addEventListener("input", function () {
      nnState.customHeightMm = Math.min(500, Math.max(10, parseFloat(el.nnCustomHeight.value) || 60));
    });
  }
  // Row add / add all
  if (el.nnAddRow) {
    el.nnAddRow.addEventListener("click", function () { addNnRow("", ""); });
  }
  if (el.nnAddAll) {
    el.nnAddAll.addEventListener("click", function () { addAllNnToSheet(); });
  }
  // Paste modal
  function openNnPaste() {
    if (!el.nnPasteModal) return;
    if (el.nnPasteInput) el.nnPasteInput.value = "";
    if (el.nnPasteCount) el.nnPasteCount.textContent = "0 rows detected";
    if (el.nnPasteImport) el.nnPasteImport.disabled = true;
    el.nnPasteModal.hidden = false;
    el.nnPasteModal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    if (el.nnPasteInput) el.nnPasteInput.focus();
  }
  function closeNnPaste() {
    if (!el.nnPasteModal) return;
    el.nnPasteModal.hidden = true;
    el.nnPasteModal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }
  if (el.nnPasteOpen) el.nnPasteOpen.addEventListener("click", openNnPaste);
  if (el.nnPasteCancels) el.nnPasteCancels.forEach(function (btn) { btn.addEventListener("click", closeNnPaste); });
  if (el.nnPasteInput) {
    el.nnPasteInput.addEventListener("input", function () {
      const parsed = parseNnPaste(el.nnPasteInput.value);
      if (el.nnPasteCount) el.nnPasteCount.textContent = parsed.length + " row" + (parsed.length === 1 ? "" : "s") + " detected";
      if (el.nnPasteImport) el.nnPasteImport.disabled = parsed.length === 0;
    });
  }
  if (el.nnPasteImport) {
    el.nnPasteImport.addEventListener("click", function () {
      const parsed = parseNnPaste(el.nnPasteInput ? el.nnPasteInput.value : "");
      // If the row list currently has only a single blank row, remove it
      if (nnState.rows.length === 1) {
        const first = nnState.rows[0];
        if (!first.name && !first.number) {
          nnState.rows.length = 0;
          if (el.nnRowList) el.nnRowList.innerHTML = "";
        }
      }
      parsed.forEach(function (r) { addNnRow(r.name, r.number); });
      closeNnPaste();
    });
  }

  // Clean-background modal wiring
  if (el.cleanBgThreshold) {
    el.cleanBgThreshold.addEventListener("input", function () {
      if (!cleanBgState) return;
      cleanBgState.threshold = parseInt(el.cleanBgThreshold.value, 10) || 0;
      if (el.cleanBgThresholdValue) el.cleanBgThresholdValue.textContent = String(cleanBgState.threshold);
      renderCleanBgAfter();
    });
  }
  if (el.cleanBgFeather) {
    el.cleanBgFeather.addEventListener("input", function () {
      if (!cleanBgState) return;
      cleanBgState.feather = parseInt(el.cleanBgFeather.value, 10) || 0;
      if (el.cleanBgFeatherValue) el.cleanBgFeatherValue.textContent = String(cleanBgState.feather);
      renderCleanBgAfter();
    });
  }
  if (el.cleanBgModeTabs && el.cleanBgModeTabs.length) {
    el.cleanBgModeTabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        if (!cleanBgState) return;
        cleanBgState.mode = tab.getAttribute("data-gsb-cleanbg-mode") || "black";
        el.cleanBgModeTabs.forEach(function (t) {
          t.classList.toggle("is-active", t === tab);
        });
        renderCleanBgAfter();
      });
    });
  }
  if (el.cleanBgApply) {
    el.cleanBgApply.addEventListener("click", function () {
      applyCleanBg();
    });
  }
  if (el.cleanBgCancels && el.cleanBgCancels.length) {
    el.cleanBgCancels.forEach(function (btn) {
      btn.addEventListener("click", function () {
        closeCleanBgModal();
      });
    });
  }
  // Esc closes the clean-bg modal
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && cleanBgState) {
      closeCleanBgModal();
    }
  });

  // Initial render
  repackAndRender();
})();

